// ============================================================================
// Electrical Schematics Course Curriculum Data
// Comprehensive Blueprint and Schematic Reading Curriculum (IEEE/ANSI Standards)
// ============================================================================

export interface CourseQuizQuestion {
  id: string;
  prompt: string;
  options: string[];
  correctIndex: number;
  explanation: string;
}

export interface CourseLesson {
  id: string;
  title: string;
  durationMinutes: number;
  summary: string;
  diagramUrl?: string;
  diagramCaption?: string;
  sections: {
    heading: string;
    content: string;
    callout?: {
      type: 'tip' | 'warning' | 'weird' | 'flow' | 'remember';
      title: string;
      text: string;
    };
  }[];
  quiz: CourseQuizQuestion[];
}

export interface CourseModule {
  id: string;
  number: number;
  title: string;
  description: string;
  lessons: CourseLesson[];
}

export interface SchematicSymbolItem {
  name: string;
  category: 'Passives' | 'Semiconductors' | 'Tubes' | 'Switches & Relays' | 'Meters & Sources' | 'Cables & Wiring';
  letterCode: string;
  description: string;
  svgIcon: string;
}

// ============================================================================
// SCHEMATIC SYMBOLS DICTIONARY (Appendix A & Table 4-1)
// ============================================================================
export const SCHEMATIC_SYMBOLS: SchematicSymbolItem[] = [
  {
    name: 'Fixed Resistor',
    category: 'Passives',
    letterCode: 'R',
    description: 'Impairs current flow. Standard zig-zag symbol (US) or rectangle (IEC).',
    svgIcon: '<path d="M 5 20 L 20 20 L 25 10 L 35 30 L 45 10 L 55 30 L 65 10 L 70 20 L 85 20" fill="none" stroke="#fbbf24" stroke-width="2.5" />'
  },
  {
    name: 'Potentiometer (3-Terminal)',
    category: 'Passives',
    letterCode: 'R',
    description: 'Three-terminal adjustable voltage divider with movable wiper contact.',
    svgIcon: '<path d="M 5 20 L 20 20 L 25 10 L 35 30 L 45 10 L 55 30 L 65 10 L 70 20 L 85 20" fill="none" stroke="#fbbf24" stroke-width="2" /><line x1="45" y1="36" x2="45" y2="24" stroke="#38bdf8" stroke-width="2" marker-end="url(#arrow)" />'
  },
  {
    name: 'Rheostat (Variable Resistor)',
    category: 'Passives',
    letterCode: 'R',
    description: 'Two-terminal wirewound variable resistor, adjusts in steps with coil inductance.',
    svgIcon: '<path d="M 5 20 L 20 20 L 25 10 L 35 30 L 45 10 L 55 30 L 65 10 L 70 20 L 85 20" fill="none" stroke="#fbbf24" stroke-width="2" /><line x1="20" y1="35" x2="70" y2="5" stroke="#34d399" stroke-width="2" marker-end="url(#arrow-emerald)" />'
  },
  {
    name: 'Fixed Capacitor (Nonpolarized)',
    category: 'Passives',
    letterCode: 'C',
    description: 'Stores energy in electric field. Blocks DC, passes AC. Curved plate faces ground.',
    svgIcon: '<line x1="10" y1="20" x2="35" y2="20" stroke="#f1f5f9" stroke-width="2" /><line x1="35" y1="5" x2="35" y2="35" stroke="#f1f5f9" stroke-width="3" /><path d="M 45 5 A 30 30 0 0 1 45 35" fill="none" stroke="#38bdf8" stroke-width="3" /><line x1="48" y1="20" x2="75" y2="20" stroke="#f1f5f9" stroke-width="2" />'
  },
  {
    name: 'Polarized Capacitor (Electrolytic)',
    category: 'Passives',
    letterCode: 'C',
    description: 'Must observe +/- polarity; reverse bias destroys oxide layer causing explosion.',
    svgIcon: '<text x="25" y="12" fill="#ef4444" font-size="12" font-weight="bold">+</text><line x1="10" y1="20" x2="35" y2="20" stroke="#f1f5f9" stroke-width="2" /><line x1="35" y1="8" x2="35" y2="32" stroke="#ef4444" stroke-width="3" /><path d="M 45 8 A 24 24 0 0 1 45 32" fill="none" stroke="#f1f5f9" stroke-width="3" /><line x1="48" y1="20" x2="75" y2="20" stroke="#f1f5f9" stroke-width="2" />'
  },
  {
    name: 'Air Variable Capacitor',
    category: 'Passives',
    letterCode: 'C',
    description: 'Rotor and stator plates with air dielectric. Rotor connects to grounded control shaft.',
    svgIcon: '<line x1="10" y1="20" x2="35" y2="20" stroke="#f1f5f9" stroke-width="2" /><line x1="35" y1="8" x2="35" y2="32" stroke="#f1f5f9" stroke-width="3" /><path d="M 45 8 A 24 24 0 0 1 45 32" fill="none" stroke="#fbbf24" stroke-width="3" /><line x1="48" y1="20" x2="75" y2="20" stroke="#f1f5f9" stroke-width="2" /><line x1="25" y1="35" x2="55" y2="5" stroke="#fbbf24" stroke-width="2" marker-end="url(#arrow-amber)" />'
  },
  {
    name: 'Air-Core Inductor',
    category: 'Passives',
    letterCode: 'L',
    description: 'Coil wound without ferromagnetic core. Zero hysteresis loss at high RF frequencies.',
    svgIcon: '<path d="M 10 20 L 20 20 A 7 7 0 0 1 34 20 A 7 7 0 0 1 48 20 A 7 7 0 0 1 62 20 L 75 20" fill="none" stroke="#38bdf8" stroke-width="2.5" />'
  },
  {
    name: 'Iron-Core Inductor (Choke)',
    category: 'Passives',
    letterCode: 'L',
    description: 'Two solid lines indicate laminated iron core for high inductance at 60 Hz power.',
    svgIcon: '<line x1="20" y1="10" x2="65" y2="10" stroke="#f1f5f9" stroke-width="1.5" /><line x1="20" y1="13" x2="65" y2="13" stroke="#f1f5f9" stroke-width="1.5" /><path d="M 10 23 L 20 23 A 7 7 0 0 1 34 23 A 7 7 0 0 1 48 23 A 7 7 0 0 1 62 23 L 75 23" fill="none" stroke="#fbbf24" stroke-width="2.5" />'
  },
  {
    name: 'Powdered-Iron Core Inductor',
    category: 'Passives',
    letterCode: 'L',
    description: 'Dashed parallel lines denote microscopic insulated iron granules for RF circuits.',
    svgIcon: '<line x1="20" y1="10" x2="65" y2="10" stroke="#34d399" stroke-width="1.5" stroke-dasharray="3,2" /><line x1="20" y1="13" x2="65" y2="13" stroke="#34d399" stroke-width="1.5" stroke-dasharray="3,2" /><path d="M 10 23 L 20 23 A 7 7 0 0 1 34 23 A 7 7 0 0 1 48 23 A 7 7 0 0 1 62 23 L 75 23" fill="none" stroke="#34d399" stroke-width="2.5" />'
  },
  {
    name: 'Roller Inductor',
    category: 'Passives',
    letterCode: 'L',
    description: 'Rotating cylindrical coil with rolling wheel contact for continuous RF antenna tuning.',
    svgIcon: '<path d="M 10 20 L 20 20 A 7 7 0 0 1 34 20 A 7 7 0 0 1 48 20 A 7 7 0 0 1 62 20 L 75 20" fill="none" stroke="#38bdf8" stroke-width="2" /><line x1="41" y1="36" x2="41" y2="24" stroke="#fbbf24" stroke-width="2" marker-end="url(#arrow-amber)" />'
  },
  {
    name: 'Transformer (Iron-Core)',
    category: 'Passives',
    letterCode: 'T',
    description: 'Mutual inductive coupling with primary and secondary windings (Vs/Vp = Ns/Np).',
    svgIcon: '<path d="M 25 8 A 5 6 0 0 1 25 18 A 5 6 0 0 1 25 28 A 5 6 0 0 1 25 38" fill="none" stroke="#38bdf8" stroke-width="2" /><line x1="32" y1="5" x2="32" y2="40" stroke="#f1f5f9" stroke-width="1.5" /><line x1="35" y1="5" x2="35" y2="40" stroke="#f1f5f9" stroke-width="1.5" /><path d="M 42 8 A 5 6 0 0 0 42 18 A 5 6 0 0 0 42 28 A 5 6 0 0 0 42 38" fill="none" stroke="#fbbf24" stroke-width="2" />'
  },
  {
    name: 'Semiconductor Diode',
    category: 'Semiconductors',
    letterCode: 'D',
    description: 'P-N junction conducts when anode is positive (forward bias); breakover ~0.7V.',
    svgIcon: '<line x1="10" y1="20" x2="32" y2="20" stroke="#f1f5f9" stroke-width="2" /><polygon points="32,10 52,20 32,30" fill="#38bdf8" /><line x1="52" y1="8" x2="52" y2="32" stroke="#f1f5f9" stroke-width="2.5" /><line x1="52" y1="20" x2="75" y2="20" stroke="#f1f5f9" stroke-width="2" />'
  },
  {
    name: 'Zener Diode',
    category: 'Semiconductors',
    letterCode: 'D, ZD',
    description: 'Operates in reverse avalanche breakdown to provide rock-solid voltage regulation.',
    svgIcon: '<line x1="10" y1="20" x2="32" y2="20" stroke="#f1f5f9" stroke-width="2" /><polygon points="32,10 52,20 32,30" fill="#fbbf24" /><path d="M 48 8 L 52 10 L 52 30 L 56 32" fill="none" stroke="#f1f5f9" stroke-width="2" /><line x1="52" y1="20" x2="75" y2="20" stroke="#f1f5f9" stroke-width="2" />'
  },
  {
    name: 'Varactor Diode',
    category: 'Semiconductors',
    letterCode: 'D',
    description: 'Acts as a voltage-controlled variable capacitor under reverse-bias voltage.',
    svgIcon: '<line x1="10" y1="20" x2="30" y2="20" stroke="#f1f5f9" stroke-width="2" /><polygon points="30,10 46,20 30,30" fill="#34d399" /><line x1="46" y1="8" x2="46" y2="32" stroke="#f1f5f9" stroke-width="2" /><line x1="52" y1="8" x2="52" y2="32" stroke="#f1f5f9" stroke-width="2" /><line x1="52" y1="20" x2="75" y2="20" stroke="#f1f5f9" stroke-width="2" />'
  },
  {
    name: 'Silicon-Controlled Rectifier (SCR)',
    category: 'Semiconductors',
    letterCode: 'SCR',
    description: 'Thyristor switch with Anode, Cathode, and Gate; latches ON until current drops to zero.',
    svgIcon: '<circle cx="42" cy="20" r="16" fill="none" stroke="#475569" stroke-width="1.5" /><polygon points="32,12 48,20 32,28" fill="#ef4444" /><line x1="48" y1="10" x2="48" y2="30" stroke="#f1f5f9" stroke-width="2" /><line x1="44" y1="24" x2="32" y2="34" stroke="#ef4444" stroke-width="1.5" />'
  },
  {
    name: 'NPN Bipolar Transistor',
    category: 'Semiconductors',
    letterCode: 'Q',
    description: 'Base current controls Collector-Emitter current. Emitter arrow points OUT (Not In).',
    svgIcon: '<circle cx="42" cy="20" r="16" fill="#0f172a" stroke="#475569" stroke-width="1.5" /><line x1="33" y1="11" x2="33" y2="29" stroke="#f1f5f9" stroke-width="2" /><line x1="15" y1="20" x2="33" y2="20" stroke="#f1f5f9" stroke-width="1.5" /><line x1="33" y1="15" x2="48" y2="8" stroke="#f1f5f9" stroke-width="1.5" /><line x1="33" y1="25" x2="48" y2="32" stroke="#38bdf8" stroke-width="1.5" marker-end="url(#arrow)" />'
  },
  {
    name: 'PNP Bipolar Transistor',
    category: 'Semiconductors',
    letterCode: 'Q',
    description: 'Requires negative collector voltage. Emitter arrow points IN toward Base (Pointing In).',
    svgIcon: '<circle cx="42" cy="20" r="16" fill="#0f172a" stroke="#475569" stroke-width="1.5" /><line x1="33" y1="11" x2="33" y2="29" stroke="#f1f5f9" stroke-width="2" /><line x1="15" y1="20" x2="33" y2="20" stroke="#f1f5f9" stroke-width="1.5" /><line x1="48" y1="8" x2="35" y2="15" stroke="#fbbf24" stroke-width="1.5" marker-end="url(#arrow-amber)" /><line x1="33" y1="25" x2="48" y2="32" stroke="#f1f5f9" stroke-width="1.5" />'
  },
  {
    name: 'N-Channel JFET',
    category: 'Semiconductors',
    letterCode: 'Q',
    description: 'Voltage on Gate constricts Drain-Source channel like stepping on a garden hose.',
    svgIcon: '<circle cx="42" cy="20" r="16" fill="#0f172a" stroke="#475569" stroke-width="1.5" /><line x1="44" y1="10" x2="44" y2="30" stroke="#f1f5f9" stroke-width="2.5" /><line x1="15" y1="20" x2="40" y2="20" stroke="#34d399" stroke-width="1.5" marker-end="url(#arrow-emerald)" /><line x1="44" y1="13" x2="58" y2="13" stroke="#f1f5f9" stroke-width="1.5" /><line x1="44" y1="27" x2="58" y2="27" stroke="#f1f5f9" stroke-width="1.5" />'
  },
  {
    name: 'Operational Amplifier (Op-Amp)',
    category: 'Semiconductors',
    letterCode: 'U, IC',
    description: 'High-gain differential IC with Inverting (-) and Non-inverting (+) inputs and output.',
    svgIcon: '<polygon points="25,5 65,22 25,39" fill="#0f172a" stroke="#38bdf8" stroke-width="2" /><text x="32" y="15" fill="#f1f5f9" font-size="10">-</text><text x="32" y="33" fill="#f1f5f9" font-size="10">+</text><line x1="10" y1="12" x2="25" y2="12" stroke="#f1f5f9" stroke-width="1.5" /><line x1="10" y1="30" x2="25" y2="30" stroke="#f1f5f9" stroke-width="1.5" /><line x1="65" y1="22" x2="80" y2="22" stroke="#f1f5f9" stroke-width="1.5" />'
  },
  {
    name: 'SPST Switch',
    category: 'Switches & Relays',
    letterCode: 'S',
    description: 'Single-Pole Single-Throw: Two-position switch (on/off or make/break).',
    svgIcon: '<circle cx="20" cy="22" r="3" fill="#f1f5f9" /><circle cx="65" cy="22" r="3" fill="#f1f5f9" /><line x1="20" y1="22" x2="55" y2="10" stroke="#38bdf8" stroke-width="2.5" />'
  },
  {
    name: 'SPDT Switch',
    category: 'Switches & Relays',
    letterCode: 'S',
    description: 'Single-Pole Double-Throw: Switches one incoming line between two alternate paths.',
    svgIcon: '<circle cx="20" cy="22" r="3" fill="#f1f5f9" /><circle cx="65" cy="12" r="3" fill="#f1f5f9" /><circle cx="65" cy="32" r="3" fill="#f1f5f9" /><line x1="20" y1="22" x2="55" y2="12" stroke="#fbbf24" stroke-width="2.5" />'
  },
  {
    name: 'SPDT Electromagnetic Relay',
    category: 'Switches & Relays',
    letterCode: 'K, RY',
    description: 'Electromagnet coil pulls armature lever to switch contacts remotely.',
    svgIcon: '<path d="M 20 28 A 4 4 0 0 1 30 28 A 4 4 0 0 1 40 28 A 4 4 0 0 1 50 28" fill="none" stroke="#38bdf8" stroke-width="2" /><circle cx="20" cy="10" r="3" fill="#f1f5f9" /><circle cx="60" cy="5" r="3" fill="#f1f5f9" /><circle cx="60" cy="16" r="3" fill="#f1f5f9" /><line x1="20" y1="10" x2="50" y2="5" stroke="#fbbf24" stroke-width="2" />'
  },
  {
    name: 'Morse Code Straight Key',
    category: 'Switches & Relays',
    letterCode: 'S, Key',
    description: 'Spring-loaded manual SPST contact for transmitting Morse code pulses.',
    svgIcon: '<circle cx="20" cy="24" r="3" fill="#f1f5f9" /><circle cx="65" cy="24" r="3" fill="#f1f5f9" /><line x1="20" y1="24" x2="55" y2="14" stroke="#f1f5f9" stroke-width="2.5" /><circle cx="45" cy="11" r="4" fill="#38bdf8" />'
  },
  {
    name: 'Triode Vacuum Tube',
    category: 'Tubes',
    letterCode: 'V',
    description: 'Thermionic tube with heated Cathode, Control Grid screen, and Anode (Plate).',
    svgIcon: '<circle cx="42" cy="20" r="18" fill="#0f172a" stroke="#cbd5e1" stroke-width="1.5" /><line x1="32" y1="10" x2="52" y2="10" stroke="#ef4444" stroke-width="2" /><line x1="42" y1="10" x2="42" y2="0" stroke="#f1f5f9" stroke-width="1.5" /><line x1="30" y1="20" x2="54" y2="20" stroke="#fbbf24" stroke-width="1.5" stroke-dasharray="3,2" /><path d="M 34 30 A 8 8 0 0 1 50 30" fill="none" stroke="#38bdf8" stroke-width="2" />'
  },
  {
    name: 'Electrochemical Battery',
    category: 'Meters & Sources',
    letterCode: 'B',
    description: 'Multicell DC source. Alternating long (+) and short thick (-) plate lines.',
    svgIcon: '<line x1="10" y1="20" x2="28" y2="20" stroke="#f1f5f9" stroke-width="2" /><line x1="28" y1="10" x2="28" y2="30" stroke="#34d399" stroke-width="3" /><line x1="36" y1="14" x2="36" y2="26" stroke="#f1f5f9" stroke-width="2" /><line x1="44" y1="10" x2="44" y2="30" stroke="#34d399" stroke-width="3" /><line x1="52" y1="14" x2="52" y2="26" stroke="#f1f5f9" stroke-width="2" /><line x1="52" y1="20" x2="70" y2="20" stroke="#f1f5f9" stroke-width="2" /><text x="24" y="9" fill="#34d399" font-size="9">+</text>'
  },
  {
    name: 'Voltmeter / Ammeter',
    category: 'Meters & Sources',
    letterCode: 'M',
    description: 'Circle with pointer arrow and scale letter (V = Volt, A = Ampere, mA = Milliammeter).',
    svgIcon: '<circle cx="42" cy="20" r="16" fill="#0f172a" stroke="#cbd5e1" stroke-width="1.5" /><line x1="15" y1="20" x2="26" y2="20" stroke="#f1f5f9" stroke-width="2" /><line x1="58" y1="20" x2="70" y2="20" stroke="#f1f5f9" stroke-width="2" /><line x1="34" y1="26" x2="49" y2="13" stroke="#38bdf8" stroke-width="1.5" marker-end="url(#arrow)" /><text x="42" y="32" fill="#cbd5e1" font-size="8" text-anchor="middle">V</text>'
  },
  {
    name: 'Shielded Coaxial Cable',
    category: 'Cables & Wiring',
    letterCode: 'W, Cable',
    description: 'Center signal conductor insulated by polyethylene inside grounded braided shield.',
    svgIcon: '<line x1="10" y1="20" x2="75" y2="20" stroke="#38bdf8" stroke-width="2" /><ellipse cx="42" cy="20" rx="10" ry="14" fill="none" stroke="#f1f5f9" stroke-width="1.5" /><circle cx="42" cy="6" r="2.5" fill="#f1f5f9" /><circle cx="42" cy="34" r="2.5" fill="#f1f5f9" /><line x1="42" y1="34" x2="42" y2="40" stroke="#f1f5f9" stroke-width="1.5" />'
  },
  {
    name: 'Off-Page Connector Wedge',
    category: 'Cables & Wiring',
    letterCode: 'X, Y, Z',
    description: 'Wedge-shaped pointer with index letter connecting signals across multi-page schematics.',
    svgIcon: '<polygon points="20,10 50,10 65,22 50,34 20,34" fill="#0f172a" stroke="#fbbf24" stroke-width="2" /><text x="38" y="26" fill="#fbbf24" font-size="12" font-weight="bold" text-anchor="middle">X</text><line x1="65" y1="22" x2="80" y2="22" stroke="#f1f5f9" stroke-width="2" />'
  }
];

// ============================================================================
// COMPLETE 6-MODULE COURSE CURRICULUM
// ============================================================================
export const COURSE_MODULES: CourseModule[] = [
  // --------------------------------------------------------------------------
  // MODULE 1: The Master Plan & Engineering Diagram Types
  // --------------------------------------------------------------------------
  {
    id: 'mod-1',
    number: 1,
    title: 'The Master Plan & Types of Diagrams',
    description: 'Learn to distinguish block diagrams, schematics, and pictorials, and master the visual language of electrical circuits.',
    lessons: [
      {
        id: 'les-1-1',
        title: 'The Three Essential Technical Diagrams',
        durationMinutes: 15,
        summary: 'Understand the distinct roles of Block Diagrams, Schematic Diagrams, and Pictorial Layouts in electrical maintenance.',
        diagramUrl: '/diagrams/diag_schematic_drawing_types_comparison.svg',
        diagramCaption: 'Figure 1-1, 1-2, 1-3 — Comparison of Block, Schematic, and Pictorial Diagrams.',
        sections: [
          {
            heading: 'The Three Drawing Styles in Electronics Literature',
            content: `Every electric and electronic device is documented with three distinct types of drawings. Each style serves an essential, specialized purpose during design, assembly, and maintenance:

1. **The Block Diagram**: Gives a macro overview of how individual circuits in a system interact. Each functional stage is rendered as a labeled block (rectangle). Connecting lines, often with arrows, show how signals and electrical power travel through the machine.
2. **The Schematic Diagram (Schematic)**: Acts as the comprehensive 'road map' of the circuit. It depicts every individual electronic component using standardized symbols and shows the exact electrical connectivity between every pin, terminal, and wire.
3. **The Pictorial (Layout) Diagram**: Shows the real-world, physical three-dimensional arrangement of components on a circuit board or equipment chassis. Pictorials allow technicians to physically locate parts for replacement and inspect wire routing.`,
            callout: {
              type: 'tip',
              title: 'The Technician’s Troubleshooting Hierarchy',
              text: 'When troubleshooting a failed machine: 1) Start with the Block Diagram to isolate the malfunctioning stage. 2) Consult the Schematic Diagram to test circuit test points and pinpoint the exact defective component. 3) Refer to the Pictorial Diagram to locate that component physically inside the enclosure for replacement.'
            }
          },
          {
            heading: 'Why Pictures Cannot Replace Schematics',
            content: `Technicians frequently ask why engineers do not just provide photographs or 3D drawings of circuit boards. Photographs show what components look like on the outside, but almost never reveal the electrical events occurring inside.

A photographic layout displays a confusing maze of copper traces, multi-layer vias, and packed surface-mount ICs. Without a schematic, you cannot tell which components are in series, which are in parallel, or what functional relationships exist. The schematic strips away physical size and 3D positioning to present pure electrical logic.`
          }
        ],
        quiz: [
          {
            id: 'q-1-1-1',
            prompt: 'When beginning troubleshooting on a complex, unfamiliar automated machine that has failed, which diagram should a technician consult first?',
            options: [
              'The Block Diagram, to identify signal flow and isolate which functional section has stopped working',
              'The Pictorial Diagram, to measure physical wire lengths with a ruler',
              'The Component Parts List, to order all replacement capacitors',
              'The Bill of Materials invoice'
            ],
            correctIndex: 0,
            explanation: 'Technical Reference: The block diagram presents the high-level functional stages. Isolating the problem to a specific block prevents wasting hours testing hundreds of individual components in the wrong circuit section.'
          }
        ]
      },
      {
        id: 'les-1-2',
        title: 'Schematics as a Visual Language & Road Map',
        durationMinutes: 12,
        summary: 'Explore why circuit schematics are analogous to road maps and why standard industrial schematics avoid color.',
        sections: [
          {
            heading: 'The Road Map Analogy',
            content: `A schematic diagram functions exactly like a highway road map. A line on a road map representing Interstate 80 does not physically look like 4 lanes of concrete and asphalt; it is an abstract human-engineered symbol. You only need to know that the line symbolizes the highway.

Similarly, an electronic schematic uses symbols to represent electrical components. A zig-zag line represents a resistor, and two parallel plates represent a capacitor. Just as a road map allows a driver to plan a cross-country route without memorizing every mile of pavement, a schematic enables an electrician to trace electrical current through complex industrial equipment at a glance.`,
            callout: {
              type: 'weird',
              title: 'Ingredients vs. Recipe',
              text: 'Listing parts (e.g. two 120-ohm resistors, one transistor, two capacitors) tells you the "ingredients", but nothing about what the circuit does! The same components can be wired into an audio oscillator, an RF amplifier, or a digital flip-flop. The schematic provides the recipe and the map.'
            }
          },
          {
            heading: 'Why Schematics Rarely Include Color',
            content: `In everyday life, humans rely on color: red means STOP or danger, green means GO. However, schematic diagrams in technical service manuals and industrial equipment cabinets almost NEVER use color.

Industrial schematics must be reproducible via monochrome photocopying, faxing, microfiche, and black-and-white printing without losing critical data. If an electrical connection relied on a line being 'red' instead of 'black', a black-and-white photocopy would render the schematic unreadable or dangerous. Engineers therefore convey all meaning strictly through geometric shapes, line weights, and standardized letter-number callouts.`
          }
        ],
        quiz: [
          {
            id: 'q-1-2-1',
            prompt: 'Why do formal industrial and military schematic diagrams avoid using color to indicate different circuit functions?',
            options: [
              'Schematics must remain 100% legible and accurate when photocopied, printed in monochrome, or viewed on black-and-white terminals',
              'Color inks cause capacitive leakage across technical blueprint paper',
              'OSHA regulations prohibit colored printing inside machine control panels',
              'Color is only allowed on hydraulic schematics, never electrical'
            ],
            correctIndex: 0,
            explanation: 'Technical Reference: Standard engineering practice mandates that all information be conveyed by symbol geometry and text designators, ensuring safety and clarity regardless of reproduction media.'
          }
        ]
      },
      {
        id: 'les-1-3',
        title: 'Electrical Conductors, Crossing Rules & Junctions',
        durationMinutes: 18,
        summary: 'Master the rules for drawing and reading crossing wires, T-junctions, split-conductors, and vintage jogs.',
        diagramUrl: '/diagrams/diag_crossing_conductors_rules.svg',
        diagramCaption: 'Figure 3-33, 3-34, 3-35 — Modern Crossing Conventions vs Connected Junctions and Vintage Jogs.',
        sections: [
          {
            heading: 'Conductors Crossing Without Connection',
            content: `Real-world circuits exist in three dimensions, but schematics must be drawn on a two-dimensional flat plane. Consequently, lines must frequently cross each other on paper even when the actual wires in the machine never touch.

In modern drafting practice:
- **Crossing Without Contact (Fig 3-33)**: Two solid lines cross at 90 degrees with **NO DOT**. This means the conductors bypass each other in 3D space with zero electrical connection.
- **Vintage Half-Loop / Jog (Fig 3-35)**: In older schematics (pre-1980s), draftsmen drew a curved half-loop or 'jog' where one wire jumped over the other. While no longer required by modern CAD standards, this vintage convention remains completely unambiguous when maintaining legacy equipment.`,
            callout: {
              type: 'warning',
              title: 'Beware the Smudge / Missing Dot Trap',
              text: 'In poor quality prints, a 4-way crossing with a dot can lose its dot (making a connection look unconnected), or a stray toner speck can create a false dot where none was intended. This is why many engineering standards strictly prohibit 4-way dot junctions!'
            }
          },
          {
            heading: 'The Preferred Split-Conductor (Offset T-Junction)',
            content: `To guarantee zero ambiguity, modern technical standards (IEEE/ANSI) recommend the **Split-Conductor (Offset T-Junction)** shown in Figure 3-34A:

Instead of having two crossing wires meet at a single shared point with a dot, the incoming branch line is staggered into two separate 'T' junctions. Because a 'T' junction cannot be confused with a crossing bypass, a technician can instantly verify that a true electrical node exists, even if the printing quality is degraded.`
          }
        ],
        quiz: [
          {
            id: 'q-1-3-1',
            prompt: 'In a modern schematic drawing, wire A crosses perpendicular to wire B. There is NO dot at the crossing. What is the electrical relationship between wire A and wire B?',
            options: [
              'Wire A and Wire B are electrically isolated from each other (they do not connect)',
              'Wire A is welded directly to Wire B',
              'Wire A carries 120VAC and Wire B carries 24VDC common',
              'Wire A is grounded through Wire B'
            ],
            correctIndex: 0,
            explanation: 'Schematic Standards: A clean 4-way cross without a junction dot means the conductors bypass each other in three-dimensional space without making contact.'
          }
        ]
      },
      {
        id: 'les-1-4',
        title: 'Component Designators & Symbology Variations',
        durationMinutes: 15,
        summary: 'Learn industry letter codes (Table 4-1) and recognize circle vs circle-free component variations.',
        sections: [
          {
            heading: 'Standard Component Letter Abbreviations (Table 4-1)',
            content: `Every component in a schematic is identified by a standardized alphanumeric designator. The letter indicates the component class, and the number indicates its sequential order in the bill of materials:

| Letter | Component Type | Example |
| :--- | :--- | :--- |
| **R** | Resistor | R1, R2, R101 |
| **C** | Capacitor | C1, C2, C12 |
| **L** | Inductor / Choke | L1, L2, RFC1 |
| **D / CR** | Diode / Rectifier | D1, D5, CR2 |
| **Q** | Transistor (Bipolar, FET) | Q1, Q2, Q104 |
| **T** | Transformer | T1, T2 |
| **K / RY** | Relay | K1, RY2 |
| **S** | Switch or Key | S1, S2, PB1 |
| **F** | Fuse | F1 (0.5A 250V) |
| **U / IC** | Integrated Circuit | U1, IC2 |
| **TP** | Test Point | TP1, TP2, TP3 |
| **SPK** | Speaker | SPKR1 |
| **ANT** | Antenna | ANT1 |`,
            callout: {
              type: 'tip',
              title: 'Letter Conflict Rules',
              text: 'Why is a relay designated K rather than R? Because R is reserved for Resistor. Why is a crystal designated Y? Because C is reserved for Capacitor. Memorize these distinct class codes!'
            }
          },
          {
            heading: 'Symbology Variations: Circle vs Circle-Free',
            content: `In early electronics, vacuum tubes were drawn inside a glass envelope represented by a circle. When solid-state transistors and SCRs were invented, draftsmen continued enclosing them in a circle (Fig. 1-4A).

However, modern computer-generated schematics often omit the outer circle (Fig. 1-4B) to reduce visual clutter in dense diagrams. Both symbols represent the identical physical component! Do not let 'circle-free mutants' confuse your analysis.`
          }
        ],
        quiz: [
          {
            id: 'q-1-4-1',
            prompt: 'In an industrial electronic schematic, a component is labeled with the reference designator "K2". What type of component is it?',
            options: [
              'An electromagnetic relay',
              'A wirewound resistor',
              'A knife switch',
              'A ceramic capacitor'
            ],
            correctIndex: 0,
            explanation: 'Standard Component Designators (Table 4-1): The letter "K" (or "RY") is the universal industry designation for an electromagnetic relay, chosen to prevent conflict with "R" for resistor.'
          }
        ]
      }
    ]
  },

  // --------------------------------------------------------------------------
  // MODULE 2: System Architecture, Block Diagrams & Flowcharts
  // --------------------------------------------------------------------------
  {
    id: 'mod-2',
    number: 2,
    title: 'Block Diagrams, Power Supplies & Flowcharts',
    description: 'Deconstruct complex systems into functional stages, trace signal flow, and read control logic flowcharts.',
    lessons: [
      {
        id: 'les-2-1',
        title: 'Block Diagrams & Signal Flow Conventions',
        durationMinutes: 14,
        summary: 'Learn standard left-to-right signal progression and stage isolation principles.',
        sections: [
          {
            heading: 'System Decomposition into Functional Blocks',
            content: `A block diagram portrays the overall anatomy of a machine. It strips away individual resistors and wires, grouping dozens of components into single operational 'stages' or 'subsystems'.

Standard conventions:
- **Left-to-Right Flow**: The primary input enters on the left, passes through successive intermediate stages, and delivers its finished output on the right.
- **Top-to-Bottom Flow**: Power distribution or secondary feedback control often flows downwards.
- **Arrowheads**: When direction of influence or signal sequence is not obvious by instinct, arrowheads clarify exactly which stage drives the next.`
          }
        ],
        quiz: [
          {
            id: 'q-2-1-1',
            prompt: 'What is the universally accepted standard direction of primary signal progression in electronic block diagrams?',
            options: [
              'From left to right (inputs on the left, outputs on the right)',
              'From right to left (inputs on the right)',
              'Clockwise circular rotation only',
              'Random arrangement based on board geometry'
            ],
            correctIndex: 0,
            explanation: 'Technical Reference: Block diagrams and schematics follow the reading direction of Western text: signals enter at the left and progress toward the right.'
          }
        ]
      },
      {
        id: 'les-2-2',
        title: 'Power Supply Architecture & Stage Decomposition',
        durationMinutes: 16,
        summary: 'Deconstruct an AC-to-DC converter into Transformer, Rectifier, Filter, and Voltage Regulator stages.',
        diagramUrl: '/diagrams/diag_dc_power_supply_bleeder.svg',
        diagramCaption: 'Figure 2-1 & 4-13 — The Four Functional Stages of an AC-to-DC Regulated Power Supply.',
        sections: [
          {
            heading: 'The 4 Essential Stages of an AC-to-DC Power Supply',
            content: `Figure 2-1 illustrates the classic AC-to-DC converter (power supply) found in industrial controls and consumer electronics:

1. **Stage 1: Transformer**: Accepts 120 VAC utility line power and steps the voltage down (or up) to the desired alternating potential via mutual induction.
2. **Stage 2: Rectifier**: Uses semiconductor diodes (such as a full-wave bridge) to convert alternating current (AC) into pulsating direct current (DC).
3. **Stage 3: Ripple Filter**: Uses high-capacity capacitors and inductive chokes to smooth out pulsations (ripple) into flat, steady DC.
4. **Stage 4: Voltage Regulator**: Employs a Zener diode or linear regulator IC to clamp the output at a steady voltage (e.g. +12 VDC) despite AC line surges or load shifts.`,
            callout: {
              type: 'flow',
              title: 'Follow the Flow',
              text: '120 VAC Sine Wave -> Transformer Step-Down -> Full-Wave Pulsating DC -> Filtered DC with Ripple -> Regulated Flat DC Output.'
            }
          }
        ],
        quiz: [
          {
            id: 'q-2-2-1',
            prompt: 'In a standard linear DC power supply, what is the specific role of the "Filter" stage placed between the rectifier and the voltage regulator?',
            options: [
              'To smooth out pulsations (ripple) in the raw rectified DC output using capacitors and inductors',
              'To step down the incoming 120V line voltage to 18V',
              'To invert the DC back into high-frequency RF',
              'To protect the operator against ESD static discharge'
            ],
            correctIndex: 0,
            explanation: 'Technical Reference: The rectifier converts AC into pulsating DC. The filter stage (filter capacitor C1 and choke L1) stores and releases energy to smooth out the ripple pulsations.'
          }
        ]
      },
      {
        id: 'les-2-3',
        title: 'Multistage RF Systems: The AM Transmitter',
        durationMinutes: 16,
        summary: 'Trace audio and radio-frequency paths through a multistage AM radiotelegraph transmitter.',
        sections: [
          {
            heading: 'Dual Signal Paths in an AM Transmitter (Fig 2-4)',
            content: `A transmitter must generate two completely distinct forms of electrical energy and combine them:

1. **The Audio Frequency (AF) Path**:
   - **Microphone**: Converts sound waves into minute electrical AC signals.
   - **AF Preamplifier**: Boosts weak microphone signals to an intermediate line level.
   - **AF Power Amplifier**: Imparts significant electrical power ('punch') to drive modulation.
   - **AF Matching Network**: Optimizes impedance transfer into the modulator.

2. **The Radio Frequency (RF) Path**:
   - **Crystal-Controlled Oscillator**: Generates a rock-solid, precise high-frequency carrier wave using a piezoelectric quartz crystal.
   - **RF Modulator / Amplifier**: Combines the high-frequency RF carrier with the amplified AF voice signal, varying RF output power in sync with voice audio.
   - **RF Tuning Network & Antenna**: Matches transmission line impedance and radiates electromagnetic waves into free space.`
          }
        ],
        quiz: [
          {
            id: 'q-2-3-1',
            prompt: 'In a crystal-controlled radio transmitter, what component establishes the exact operating carrier frequency?',
            options: [
              'A piezoelectric quartz crystal in the oscillator circuit',
              'The microphone preamplifier gain potentiometer',
              'The audio impedance matching transformer',
              'The antenna ground rod length'
            ],
            correctIndex: 0,
            explanation: 'Technical Reference: The crystal-controlled oscillator uses the precise mechanical resonance of a quartz crystal slice to determine the exact radio transmission frequency.'
          }
        ]
      },
      {
        id: 'les-2-4',
        title: 'Control Logic Flowcharts & ANSI Standard Symbols',
        durationMinutes: 18,
        summary: 'Master the standard flowchart symbols used in programmable logic, automation, and microcomputers.',
        sections: [
          {
            heading: 'Hardware Schematics vs. Software Flowcharts',
            content: `While a schematic depicts physical hardware wiring, a **flowchart** provides a graphic map of logical steps and decisions executed by an automated controller or computer program.

Standard ANSI Flowchart Shapes (Fig. 2-6):
- **Oval**: Start or Stop terminal point.
- **Rectangle**: Processing or arithmetic calculation operation.
- **Trapezoid**: Input or Output operation (e.g. read barcode, print log).
- **Diamond**: Decision block with conditional branching (e.g. 'Is temperature > 150°F?' with YES and NO branch lines).
- **Flattened Hexagon**: Prewritten subprogram or subroutine called from memory.
- **Small Circle**: Inflow or outflow junction point (merging paths).
- **Five-Sided 'Home Plate'**: Off-page connector showing where a flowchart continues onto another page.`,
            callout: {
              type: 'tip',
              title: 'Decision Diamonds Rule',
              text: 'Every decision diamond MUST have at least two labeled exit paths (such as Yes/No or High/Low). A decision with only one exit is a logical contradiction!'
            }
          }
        ],
        quiz: [
          {
            id: 'q-2-4-1',
            prompt: 'In an ANSI standard automation flowchart, what geometric shape denotes a decision point that branches into multiple logical paths?',
            options: [
              'A Diamond',
              'A Rectangle',
              'A Trapezoid',
              'An Oval'
            ],
            correctIndex: 0,
            explanation: 'Schematic Standards: A diamond shape denotes a conditional decision, with arrowed lines exiting for each possible outcome (e.g. Yes/No).'
          }
        ]
      }
    ]
  },

  // --------------------------------------------------------------------------
  // MODULE 3: Passive Components — Resistors, Capacitors & Inductors
  // --------------------------------------------------------------------------
  {
    id: 'mod-3',
    number: 3,
    title: 'Passive Components: R, C & L Mastery',
    description: 'Master fixed and variable resistors, color code decoding, capacitors, dielectrics, inductor cores, and transformers.',
    lessons: [
      {
        id: 'les-3-1',
        title: 'Resistors, Potentiometers & Rheostats',
        durationMinutes: 16,
        summary: 'Deep dive into fixed carbon/film/wirewound resistors and understand how 3-terminal potentiometers operate.',
        diagramUrl: '/diagrams/diag_potentiometer_rheostat_anatomy.svg',
        diagramCaption: 'Figure 3-5, 3-7 — Potentiometer anatomy (wiper Y on resistive strip between X and Z).',
        sections: [
          {
            heading: 'Fixed Resistors & Construction',
            content: `A resistor impairs the flow of electric current, converting electrical energy into heat. Resistance is measured in ohms (Ω), kilohms (kΩ = 1,000 Ω), and megohms (MΩ = 1,000,000 Ω).

Common fixed constructions:
- **Carbon Composition**: Molded cylinder of carbon dust and insulating binder. Rugged, handles large momentary surges, but drifts over time.
- **Carbon / Metal Film**: Thin film deposited on a ceramic rod. Excellent precision and low temperature drift.
- **Wirewound**: Nichrome wire wound around a ceramic cylinder. Handles heavy power (5W to 100W+), but inherently possesses inductive reactance.`
          },
          {
            heading: 'The 3-Terminal Potentiometer (Fig 3-7)',
            content: `A rotary potentiometer consists of a circular resistive strip with fixed terminals at both ends (**X** and **Z**) and a rotatable shaft with a sliding contact arm called the **wiper** (**Y**).

When used as a volume control:
- Terminal X connects to incoming signal.
- Terminal Z connects to ground (0V).
- Terminal Y (wiper) taps off any fraction of the signal from 0% (at Z) to 100% (at X).`
          }
        ],
        quiz: [
          {
            id: 'q-3-1-1',
            prompt: 'In a standard 3-terminal rotary potentiometer (Fig. 3-7), which terminal corresponds to the movable slider or wiper?',
            options: [
              'Terminal Y (the center arrow contacting the resistive element)',
              'Terminal X (the fixed counter-clockwise end)',
              'Terminal Z (the fixed clockwise end)',
              'The metal mounting threaded bushing'
            ],
            correctIndex: 0,
            explanation: 'Schematic Standards: Terminals X and Z are fixed ends of the resistive track; terminal Y connects to the wiper that rotates across the track.'
          }
        ]
      },
      {
        id: 'les-3-2',
        title: 'Resistor Color Code Decoding Mastery',
        durationMinutes: 20,
        summary: 'Decode 4-band and 5-band resistor color codes, multipliers, tolerances, and reliability failure rates.',
        diagramUrl: '/diagrams/diag_resistor_color_code_bands.svg',
        diagramCaption: 'Appendix B, Fig B-1 — Resistor Color Code Band Layout and Multipliers.',
        sections: [
          {
            heading: 'The Standard EIA Resistor Color Code Table (Table B-1)',
            content: `Axial-lead resistors use colored bands to indicate resistance value, tolerance, and failure rate:

| Color | Digit (Bands 1 & 2) | Multiplier (Band 3) | Tolerance (Band 4) |
| :--- | :---: | :---: | :---: |
| **Black** | 0 | $\\times 1$ | — |
| **Brown** | 1 | $\\times 10$ | $\\pm 1\\%$ |
| **Red** | 2 | $\\times 100$ | $\\pm 2\\%$ |
| **Orange** | 3 | $\\times 1,000$ (1k) | — |
| **Yellow** | 4 | $\\times 10,000$ (10k) | — |
| **Green** | 5 | $\\times 100,000$ (100k) | $\\pm 0.5\\%$ |
| **Blue** | 6 | $\\times 1,000,000$ (1M) | $\\pm 0.25\\%$ |
| **Violet** | 7 | $\\times 10,000,000$ (10M) | $\\pm 0.1\\%$ |
| **Gray** | 8 | $\\times 100,000,000$ (100M) | $\\pm 0.05\\%$ |
| **White** | 9 | $\\times 1,000,000,000$ (1G) | — |
| **Gold** | — | $\\times 0.1$ | $\\pm 5\\%$ |
| **Silver** | — | $\\times 0.01$ | $\\pm 10\\%$ |
| **None** | — | — | $\\pm 20\\%$ |`,
            callout: {
              type: 'tip',
              title: 'Classic Mnemonic',
              text: 'Remember color band sequence: Black (0), Brown (1), Red (2), Orange (3), Yellow (4), Green (5), Blue (6), Violet (7), Gray (8), White (9).'
            }
          }
        ],
        quiz: [
          {
            id: 'q-3-2-1',
            prompt: 'A technician reads a 4-band resistor with bands: Blue, Gray, Orange, Silver. What is its nominal resistance and tolerance?',
            options: [
              '68,000 Ω (68 kΩ) ±10%',
              '6,800 Ω ±5%',
              '47,000 Ω ±10%',
              '680 Ω ±20%'
            ],
            correctIndex: 0,
            explanation: 'Blue = 6, Gray = 8, Orange = x1,000 (1k). Value = 68 x 1,000 = 68,000 Ω = 68 kΩ. Silver band = ±10% tolerance.'
          }
        ]
      },
      {
        id: 'les-3-3',
        title: 'Capacitors, Dielectrics & Polarity Rules',
        durationMinutes: 18,
        summary: 'Explore nonpolarized vs polarized electrolytic capacitors, explosion hazards, and variable capacitor tuning.',
        diagramUrl: '/diagrams/diag_capacitor_types_symbols.svg',
        diagramCaption: 'Figure 3-9 to 3-14 — Fixed, Polarized, Variable, and Ganged Capacitor Symbols.',
        sections: [
          {
            heading: 'Capacitance Fundamentals & Units',
            content: `A capacitor stores electrical energy in an electrostatic field between conductive plates separated by an insulator called a **dielectric**.
- The unit of capacitance is the **Farad (F)**.
- Because 1 Farad is enormous, real-world components are rated in **microfarads** ($1~\\mu\\text{F} = 10^{-6}~\\text{F}$) or **picofarads** ($1~\\text{pF} = 10^{-12}~\\text{F}$).

Capacitors block steady direct current (DC) while conducting alternating current (AC).`
          },
          {
            heading: 'Polarized Electrolytic Capacitors & Explosion Danger',
            content: `Electrolytic and tantalum capacitors achieve huge capacitance in compact cans by utilizing an electrochemically formed microscopic oxide layer.

**DANGER**: Electrolytic capacitors are polarized. They must ALWAYS be wired with the positive lead (+) at higher potential than the negative lead (-). If connected backwards, current breaks down the dielectric, generating extreme boiling heat and steam that ruptures the metal canister violently!`,
            callout: {
              type: 'warning',
              title: 'Polarity Orientation',
              text: 'Always check the schematic for the "+" sign and inspect the component stripe indicating "-" before powering on.'
            }
          }
        ],
        quiz: [
          {
            id: 'q-3-3-1',
            prompt: 'How many microfarads (µF) are in 0.005 Farads (F)?',
            options: [
              '5,000 µF',
              '50 µF',
              '500 µF',
              '50,000 µF'
            ],
            correctIndex: 0,
            explanation: '1 Farad = 1,000,000 µF. Therefore, 0.005 F × 1,000,000 = 5,000 µF.'
          }
        ]
      },
      {
        id: 'les-3-4',
        title: 'Inductors, Magnetic Cores & Transformers',
        durationMinutes: 18,
        summary: 'Compare air-core, solid iron, and powdered-iron inductors and understand transformer voltage transformation.',
        diagramUrl: '/diagrams/diag_inductor_transformer_cores.svg',
        diagramCaption: 'Figure 3-15 to 3-24 — Inductor Core Symbols and Transformer Principles.',
        sections: [
          {
            heading: 'Inductance & Magnetic Core Materials',
            content: `An inductor opposes changes in electric current by storing energy in a magnetic field. Inductance is measured in **Henrys (H)**, millihenrys (mH), and microhenrys (µH).

Core materials and their schematic symbols:
- **Air-Core (Fig 3-15)**: Clean coil symbol with no lines. Used for radio frequencies (RF) where core eddy losses must be zero.
- **Solid or Laminated Iron (Fig 3-19)**: Parallel solid lines above coil. Used for 60 Hz AC line chokes and audio. Provides massive inductance but high loss at high frequencies.
- **Powdered-Iron (Fig 3-22)**: Parallel dashed (broken) lines above coil. Finely crushed iron particles suspended in glue prevent eddy currents at high radio frequencies.`
          },
          {
            heading: 'Transformer Turns Ratio Rule',
            content: `A transformer transfers AC power between primary and secondary windings through mutual induction:

$$\\frac{V_s}{V_p} = \\frac{N_s}{N_p}$$

- **Step-Up Transformer**: More secondary turns ($N_s > N_p$), stepping output voltage UP.
- **Step-Down Transformer**: Fewer secondary turns ($N_s < N_p$), stepping output voltage DOWN.`
          }
        ],
        quiz: [
          {
            id: 'q-3-4-1',
            prompt: 'An iron-core power transformer has 1,200 primary turns connected to 120 VAC. The secondary winding has 120 turns. What is the secondary output voltage?',
            options: [
              '12 VAC (Step-down by 10:1)',
              '120 VAC',
              '1,200 VAC',
              '24 VAC'
            ],
            correctIndex: 0,
            explanation: 'Vs = Vp × (Ns / Np) = 120 VAC × (120 / 1200) = 120 × 0.1 = 12 VAC.'
          }
        ]
      }
    ]
  },

  // --------------------------------------------------------------------------
  // MODULE 4: Active Semiconductors, Op-Amps & Tubes
  // --------------------------------------------------------------------------
  {
    id: 'mod-4',
    number: 4,
    title: 'Active Semiconductors, Op-Amps & Tubes',
    description: 'Understand switches, relays, diodes, Zener regulation, BJTs, FETs, Op-Amps, active filters, and vintage vacuum tubes.',
    lessons: [
      {
        id: 'les-4-1',
        title: 'Switches, Contacts & Relays',
        durationMinutes: 15,
        summary: 'Master pole and throw configurations, rotary switches, and electromagnetic relay control.',
        sections: [
          {
            heading: 'Poles and Throws Terminology',
            content: `A switch completes or interrupts one or more electrical paths:
- **Poles**: The number of separate movable circuits switched at once.
- **Throws**: The number of closed contact positions available per pole.

Types:
- **SPST**: Single-Pole Single-Throw (simple on/off).
- **SPDT**: Single-Pole Double-Throw (toggles one line between two destinations).
- **DPDT**: Double-Pole Double-Throw (reversing switch or dual circuit toggle).
- **Rotary (1P10T)**: One pole selecting one of 10 throw contacts.`
          },
          {
            heading: 'Electromagnetic Relays for Remote Switching (Fig 3-32)',
            content: `A relay uses a low-voltage DC electromagnet coil to physically pull an iron armature lever that switches heavy load contacts.

Why use relays?
In an industrial plant, running heavy 480V or sensitive RF coaxial cable 100 meters to an operator switch is expensive and dangerous. Instead, the relay sits at the machine, and light 24VDC wiring connects to the remote control button.`
          }
        ],
        quiz: [
          {
            id: 'q-4-1-1',
            prompt: 'What type of switch is required to simultaneously reverse both the positive and negative connections of a DC motor to change its rotation direction?',
            options: [
              'A DPDT (Double-Pole Double-Throw) switch',
              'An SPST switch',
              'A 1P10T rotary switch',
              'A normally-open pushbutton'
            ],
            correctIndex: 0,
            explanation: 'Reversing two lines simultaneously requires two poles, each switching between two positions (DPDT).'
          }
        ]
      },
      {
        id: 'les-4-2',
        title: 'Diodes, Breakover, Zener & SCRs',
        durationMinutes: 18,
        summary: 'Study P-N junction conduction, breakover thresholds, Zener regulation, and SCR gate latching.',
        diagramUrl: '/diagrams/diag_diode_breakover_characteristic.svg',
        diagramCaption: 'Figure 3-39 to 3-41 — Diodes, Zener regulation, and Silicon-Controlled Rectifiers.',
        sections: [
          {
            heading: 'The P-N Junction & Breakover Threshold',
            content: `A semiconductor diode joins P-type and N-type silicon.
- **Forward Bias**: Anode positive with respect to cathode. Current conducts once forward voltage exceeds the **breakover threshold** (~0.7V for silicon).
- **Reverse Bias**: Cathode positive with respect to anode. A tiny leakage current flows until reaching the avalanche breakdown limit.`
          },
          {
            heading: 'Zener Diodes and SCRs',
            content: `- **Zener Diode (Fig 3-40B)**: Specifically engineered to break down non-destructively at a precise reverse voltage, clamping the output voltage to provide stable DC regulation.
- **Silicon-Controlled Rectifier (SCR, Fig 3-41)**: A 4-layer thyristor with Anode, Cathode, and Gate. It remains non-conductive until a momentary pulse triggers the Gate. Once triggered, the SCR latches ON and stays ON until anode current drops to zero.`
          }
        ],
        quiz: [
          {
            id: 'q-4-2-1',
            prompt: 'Once a Silicon-Controlled Rectifier (SCR) has been triggered into conduction by a gate pulse, how can it be turned OFF?',
            options: [
              'The current flowing between Anode and Cathode must be interrupted or dropped below holding current',
              'A negative pulse must be applied to the Gate',
              'The Gate terminal must be shorted to chassis ground',
              'The Cathode must be disconnected from ground'
            ],
            correctIndex: 0,
            explanation: 'Technical Reference: An SCR latches ON regenerative conduction. Removing gate current does NOT turn it off; the main anode-to-cathode current must be cut or dropped below holding threshold.'
          }
        ]
      },
      {
        id: 'les-4-3',
        title: 'Transistors: Bipolar (NPN/PNP) & Field-Effect',
        durationMinutes: 20,
        summary: 'Identify NPN vs PNP BJTs, understand JFET channel constriction, and compare depletion vs enhancement MOSFETs.',
        diagramUrl: '/diagrams/diag_transistor_bipolar_vs_fet.svg',
        diagramCaption: 'Figure 3-42 to 3-44 — Bipolar Transistors, JFETs, and MOSFETs.',
        sections: [
          {
            heading: 'Bipolar Junction Transistors (BJTs)',
            content: `BJTs use small base current to control large collector-to-emitter current:
- **NPN Transistor**: Emitter arrow points OUT (Not Pointing iN). Requires positive collector voltage (+Vcc).
- **PNP Transistor**: Emitter arrow points IN toward base (Pointing iN). Requires negative collector voltage relative to emitter.`
          },
          {
            heading: 'Field-Effect Transistors (FETs)',
            content: `Unlike current-controlled BJTs, FETs are **voltage-controlled**:
- **JFET (Junction FET)**: Applying reverse voltage to the Gate widens the internal depletion layer, constricting current flowing from Source to Drain like stepping on a garden hose.
- **Depletion-Mode MOSFET**: Normally ON at zero gate bias; gate voltage pinches off the channel.
- **Enhancement-Mode MOSFET**: Normally OFF at zero gate bias; gate voltage enhances the channel to turn it ON.`
          }
        ],
        quiz: [
          {
            id: 'q-4-3-1',
            prompt: 'Which transistor type is "normally ON" (conducts between Source and Drain with zero gate bias voltage)?',
            options: [
              'A Depletion-mode MOSFET',
              'An Enhancement-mode MOSFET',
              'A standard NPN bipolar transistor',
              'A Silicon Controlled Rectifier'
            ],
            correctIndex: 0,
            explanation: 'Technical Reference: A depletion-mode MOSFET has a conductive channel present at Vgs = 0 and requires bias voltage to pinch off current.'
          }
        ]
      },
      {
        id: 'les-4-4',
        title: 'Operational Amplifiers (Op-Amps) & Feedback',
        durationMinutes: 18,
        summary: 'Explore op-amp differential inputs, inverting vs non-inverting amplification, and negative feedback stability.',
        sections: [
          {
            heading: 'The Op-Amp IC Architecture (Fig 3-46)',
            content: `An operational amplifier (Op-Amp) is an integrated circuit with:
- **Non-Inverting Input (+)**: Output signal emerges in phase coincidence (0° shift).
- **Inverting Input (-)**: Output signal emerges in phase opposition (180° inverted).
- **Power Supplies**: Collector supply (+Vcc) and Emitter supply (-Vee).`
          },
          {
            heading: 'Open-Loop vs Closed-Loop Gain',
            content: `In the open-loop configuration (no feedback resistor), op-amp gain is virtually infinite (over 100,000). The slightest millivolt difference slams the output into rail saturation or oscillation.

In the **closed-loop configuration**, a feedback resistor ($R_f$) connects the output back to the inverting input (-). This provides **negative feedback**, stabilizing the circuit to a predictable, precise voltage gain determined entirely by the resistor ratio.`
          }
        ],
        quiz: [
          {
            id: 'q-4-4-1',
            prompt: 'In an op-amp amplifier circuit, what happens to the voltage gain when the value of the negative feedback resistor Rf between output and inverting input is REDUCED?',
            options: [
              'Voltage gain decreases because negative feedback increases',
              'Voltage gain increases towards infinity',
              'The amplifier switches to an oscillator',
              'The op-amp burns out from excessive current'
            ],
            correctIndex: 0,
            explanation: 'Technical Reference: Reducing Rf feeds more inverted signal back to the input (greater negative feedback), which directly reduces overall circuit gain.'
          }
        ]
      },
      {
        id: 'les-4-5',
        title: 'Active Filters: Lowpass, Highpass & Resonant',
        durationMinutes: 20,
        summary: 'Calculate cutoff frequencies for active RC filters and analyze resonant peak and notch responses.',
        diagramUrl: '/diagrams/diag_opamp_active_filters.svg',
        diagramCaption: 'Figure 3-47 & 5-22 to 5-25 — The Four Active Filter Topologies.',
        sections: [
          {
            heading: 'The 4 Frequency Response Curves',
            content: `By placing resistors and capacitors in the op-amp feedback network, we build frequency-selective active filters:
1. **Lowpass Filter**: Passes frequencies below cutoff $f_c$; attenuates high frequencies.
2. **Highpass Filter**: Passes frequencies above cutoff $f_c$; blocks DC and low frequencies.
3. **Resonant Peak (Bandpass)**: Amplifies a single center frequency $f_r$ while attenuating all others.
4. **Resonant Notch (Bandstop)**: Sharp null at $f_n$; eliminates specific interference (e.g. 60 Hz hum).`
          },
          {
            heading: 'The Cutoff Frequency Formula',
            content: `For lowpass and highpass active filters:

$$f_c = \\frac{1}{2\\pi R C}$$

- Increasing resistance $R$ or capacitance $C$ **lowers** the cutoff frequency.
- Decreasing $R$ or $C$ **raises** the cutoff frequency.`
          }
        ],
        quiz: [
          {
            id: 'q-4-5-1',
            prompt: 'In an active lowpass filter with R = 10,000 Ω (10 kΩ) and C = 0.0159 µF, what is the approximate cutoff frequency fc?',
            options: [
              '1,000 Hz (1 kHz)',
              '100 Hz',
              '10,000 Hz',
              '60 Hz'
            ],
            correctIndex: 0,
            explanation: 'fc = 1 / (2π × 10,000 × 1.59×10^-8) = 1 / (6.283 × 0.000159) = 1 / 0.001 ≈ 1,000 Hz.'
          }
        ]
      },
      {
        id: 'les-4-6',
        title: 'Electron Tubes & Legacy Schematics',
        durationMinutes: 16,
        summary: 'Read vintage vacuum tube symbols: diodes, triodes, tetrodes, pentodes, and gas-filled tubes.',
        sections: [
          {
            heading: 'Vacuum Tube Electrodes & Flow',
            content: `Though largely replaced by semiconductors, vacuum tubes remain critical in high-power RF transmitters, audio guitar amplifiers, and industrial radar.

Electrodes inside the glass envelope (Fig 3-48):
- **Cathode**: Heated by a filament to emit free electrons. (Directly heated = filament is cathode; Indirectly heated = insulated filament inside cathode sleeve).
- **Plate / Anode**: Carries high positive DC potential to attract electrons.
- **Control Grid**: Wire mesh between cathode and plate that regulates electron flow via negative bias.
- **Screen Grid (Tetrode)**: Reduces plate-to-grid interelectrode capacitance.
- **Suppressor Grid (Pentode)**: Connected to cathode to suppress secondary electron emission from the plate.`
          }
        ],
        quiz: [
          {
            id: 'q-4-6-1',
            prompt: 'How many grids does a Pentode vacuum tube contain between its cathode and plate?',
            options: [
              'Three grids (Control grid, Screen grid, Suppressor grid)',
              'One grid',
              'Two grids',
              'Five grids'
            ],
            correctIndex: 0,
            explanation: 'Technical Reference: A pentode has five total elements: cathode, three grids (control, screen, suppressor), and plate.'
          }
        ]
      }
    ]
  },

  // --------------------------------------------------------------------------
  // MODULE 5: Reading & Analyzing Complex Schematics
  // --------------------------------------------------------------------------
  {
    id: 'mod-5',
    number: 5,
    title: 'Reading & Analyzing Complete Schematics',
    description: 'Synthesize multi-stage schematics: DC supplies, 3-transistor AM receivers, multi-page page breaks, oscillators, and strobe circuits.',
    lessons: [
      {
        id: 'les-5-1',
        title: 'DC Power Supplies & Bleeder Resistor Life Safety',
        durationMinutes: 18,
        summary: 'Trace full-wave bridge rectifiers, choke filters, and understand why bleeder resistors save lives.',
        diagramUrl: '/diagrams/diag_dc_power_supply_bleeder.svg',
        diagramCaption: 'Figure 4-11 & 4-13 — DC Power Supply with Bleeder Resistor R1 and Zener Regulation.',
        sections: [
          {
            heading: 'Full-Wave Bridge Rectification (Fig 4-13)',
            content: `The full-wave bridge rectifier uses four diodes arranged in a diamond configuration (D1–D4):
- On positive AC half-cycles, two diodes conduct.
- On negative AC half-cycles, the other two diodes conduct.
- Both half-cycles produce current in the same direction through filter capacitor C1 and choke L1, doubling ripple frequency to 120 Hz and making it much easier to filter than half-wave 60 Hz.`
          },
          {
            heading: 'Bleeding for Your Life!',
            content: `Filter capacitors store electrical charge at full DC voltage. If a technician unplugs a power supply, a good capacitor can hold several hundred volts for hours or days!

**Bleeder Resistor R1**: Placed permanently in parallel with the filter capacitor. When utility power is switched off, R1 drains the capacitor safely down to zero volts in seconds, preventing lethal electric shock during servicing.`,
            callout: {
              type: 'remember',
              title: 'Life Safety Rule',
              text: 'Never omit or remove a bleeder resistor from a high-voltage power supply. Before touching any capacitor terminals, always meter across them to verify 0.0V DC!'
            }
          }
        ],
        quiz: [
          {
            id: 'q-5-1-1',
            prompt: 'In a 480V DC power supply, what is the primary danger if the bleeder resistor opens or is removed?',
            options: [
              'The filter capacitors will retain a lethal high-voltage charge even when the unit is unplugged from AC power',
              'The transformer primary fuse will blow immediately',
              'The rectifier diodes will short out',
              'The output voltage will drop to zero'
            ],
            correctIndex: 0,
            explanation: 'Electrical Safety Principle: Without a bleeder resistor, high-voltage capacitors can hold deadly stored energy for days after AC mains disconnection.'
          }
        ]
      },
      {
        id: 'les-5-2',
        title: 'Multistage AM Radio Receiver Deconstruction',
        durationMinutes: 22,
        summary: 'Deconstruct the complete 3-transistor AM receiver: RF tuner, diode detector, audio preamp, and push-pull power stage.',
        diagramUrl: '/diagrams/diag_am_receiver_stages.svg',
        diagramCaption: 'Figure 5-5 — Complete 3-Transistor AM Radio Receiver Schematic.',
        sections: [
          {
            heading: 'Tracing the Complete Signal Path (Fig 5-5)',
            content: `A complex schematic is simply multiple simple building blocks connected in cascade:

1. **Stage 1 (Tuner)**: Antenna intercept EM waves. Inductor L1 and variable capacitor C1 form an LC parallel resonant tank circuit selecting the station frequency.
2. **Stage 2 (Detector)**: Diode D1 demodulates the signal into pulsating DC. Capacitor C2 shunts RF carrier to ground, leaving clean audio frequency (AF).
3. **Stage 3 (AF Preamp)**: Capacitor C3 couples audio to the base of NPN transistor Q1. Q1 boosts the weak millivolt audio signal.
4. **Stage 4 (Push-Pull Power Amp)**: Audio transformer T1 splits the audio into two out-of-phase halves driving Q2 and Q3. Output transformer T2 combines them to drive loudspeaker SPKR.`
          }
        ],
        quiz: [
          {
            id: 'q-5-2-1',
            prompt: 'In the AM radio receiver schematic (Fig. 5-5), what is the function of transformer T1 in the push-pull amplifier stage?',
            options: [
              'It acts as a phase splitter to provide two equal and opposite drive signals to transistors Q2 and Q3',
              'It steps down 120VAC line power to 12VDC',
              'It grounds the antenna during lightning storms',
              'It filters out 60 Hz hum from the speaker'
            ],
            correctIndex: 0,
            explanation: 'Technical Reference: Transformer T1 has a center-tapped secondary winding that splits the incoming audio signal into two 180° out-of-phase signals to drive Q2 and Q3 in push-pull.'
          }
        ]
      },
      {
        id: 'les-5-3',
        title: 'Multi-Page Schematics & Page Break Tracing',
        durationMinutes: 16,
        summary: 'Trace electrical signals across multiple blueprint sheets using off-page connection wedges (X, Y, Z).',
        sections: [
          {
            heading: 'Off-Page Connector Pointers (Fig 5-6 & 5-13)',
            content: `Large industrial machinery and commercial transmitters cannot fit on a single page. Draftsmen split diagrams across multiple pages using **off-page connection wedges**:
- A rightward-pointing wedge labeled **X** on Page 1 connects directly to a leftward-pointing wedge labeled **X** on Page 2.
- A wedge can feed multiple destinations: for instance, DC power rail **X** on power supply sheet A connects to **X** on oscillator sheet B AND **X** on power amp sheet C.

Always locate matching letter tags across all schematic sheets before beginning continuity tests!`
          }
        ],
        quiz: [
          {
            id: 'q-5-3-1',
            prompt: 'When reading a multi-page schematic, you see a wedge arrow labeled "Y" pointing off Sheet 2, and another wedge arrow labeled "Y" on Sheet 3. What do these wedges represent?',
            options: [
              'A continuous electrical wire conductor running between Sheet 2 and Sheet 3',
              'A high-voltage spark gap',
              'A wireless RF antenna link',
              'A mechanical drive belt'
            ],
            correctIndex: 0,
            explanation: 'Technical Reference: Wedges containing matching letters represent direct electrical connections broken across page boundaries.'
          }
        ]
      },
      {
        id: 'les-5-4',
        title: 'Antenna Matching Networks: L, Pi & Pi-L',
        durationMinutes: 18,
        summary: 'Understand impedance matching networks (L-network, Pi-network, Pi-L network) and harmonic filtering.',
        sections: [
          {
            heading: 'Antenna Matching Topologies (Fig 5-7 to 5-9)',
            content: `Transmitters require an exact impedance match (typically 50 Ω) to transfer maximum power to an antenna without destructive reflected power.

Topologies:
- **L-Network (Fig 5-7)**: One inductor and one capacitor forming an 'L' shape. Simple matching for narrow frequency bands.
- **Pi-Network (Fig 5-8)**: One series inductor between two shunt variable capacitors, forming the Greek letter $\\pi$. Provides impedance matching plus significant lowpass harmonic suppression.
- **Pi-L Network (Fig 5-9)**: A Pi-network followed by an L-network. Offers superior harmonic attenuation and matches a much wider range of antenna impedances.`
          }
        ],
        quiz: [
          {
            id: 'q-5-4-1',
            prompt: 'Why is a Pi-L network (Fig. 5-9) often preferred over a simple L-network in high-power RF transmitters?',
            options: [
              'It provides much greater suppression of unwanted harmonics and matches a far wider range of complex antenna impedances',
              'It eliminates the need for an RF ground',
              'It operates with zero capacitors',
              'It converts RF power directly into audio'
            ],
            correctIndex: 0,
            explanation: 'Technical Reference: Adding the second inductor and third capacitor in a Pi-L network dramatically improves harmonic attenuation and impedance flexibility.'
          }
        ]
      },
      {
        id: 'les-5-5',
        title: 'Strobe Light Circuit Analysis & SCR Pulse Triggering',
        durationMinutes: 20,
        summary: 'Analyze the complete strobe light schematic: voltage doubler, neon relaxation oscillator, and pulse transformer.',
        sections: [
          {
            heading: 'Deconstructing the Strobe Light Schematic (Fig 5-16)',
            content: `The strobe light system provides a masterclass in reading complex industrial pulse circuits:

1. **Power & Doubler**: 120 VAC line enters through fuse F1 and switch S1. Diodes D1/D2 and capacitors C1/C2 form a voltage doubler generating ~300 VDC.
2. **Main Xenon Tube**: 300 VDC is applied continuously across Anode (A) and Cathode (K) of the xenon flash tube. The gas does not conduct yet because it requires an ionization pulse.
3. **RC Timing Network**: Resistors R1–R4 and capacitor C2 charge up slowly. Variable resistor R4 adjusts the charging time (flash rate).
4. **Neon Trigger NE1 & SCR1**: When timing voltage reaches ~90V, neon bulb NE1 fires, delivering a sharp current pulse to the Gate of SCR1.
5. **Trigger Transformer T1**: SCR1 snaps ON, discharging a capacitor through the primary of pulse transformer T1. T1 steps up the pulse to over 4,000 Volts on Trigger terminal (T), ionizing the xenon gas to produce a brilliant flash!`
          }
        ],
        quiz: [
          {
            id: 'q-5-5-1',
            prompt: 'In the strobe light schematic (Fig. 5-16), which component controls the flash repetition rate by adjusting the charging time of the timing capacitor?',
            options: [
              'Potentiometer R4',
              'Fuse F1',
              'Rectifier diode D1',
              'Xenon flash tube'
            ],
            correctIndex: 0,
            explanation: 'Technical Reference: Potentiometer R4 varies the RC time constant, adjusting how rapidly the trigger capacitor reaches neon breakdown voltage.'
          }
        ]
      }
    ]
  },

  // --------------------------------------------------------------------------
  // MODULE 6: Testing, Circuit Laws & Diagnostic Lab
  // --------------------------------------------------------------------------
  {
    id: 'mod-6',
    number: 6,
    title: 'Testing, Circuit Laws & Diagnostic Laboratory',
    description: 'Master multimeter test points, verify KCL and KVL, explore voltage divider sag vs diode regulation, and build a galvanometer.',
    lessons: [
      {
        id: 'les-6-1',
        title: 'Systematic Troubleshooting & Test Point (TP) Analysis',
        durationMinutes: 20,
        summary: 'Use VOM multimeters to test voltage test points (TP1, TP2, TP3) and isolate open vs shorted components.',
        diagramUrl: '/diagrams/diag_schematic_drawing_types_comparison.svg',
        diagramCaption: 'Figure 4-17 — Transistor Amplifier Schematic with Test Points TP1 (Emitter), TP2 (Base), and TP3 (Collector).',
        sections: [
          {
            heading: 'The Diagnostic Workflow: Forward vs. Backward Tracing',
            content: `When a circuit malfunctions, never guess or replace parts blindly. Use a systematic process of elimination:
- **Forward Tracing**: Start at the input where signal is known to be good. Probe forward test point by test point until the signal disappears or voltage deviates. The fault lies between the last good point and the first bad point!
- **Backward Tracing**: Start at the dead output and trace back towards the power source until normal voltage is discovered.
- **Component Tolerances**: In commercial electronics, normal operating voltages may vary by up to **±20%**. Readings within this band are usually acceptable; readings outside indicate an open, short, or severe drift.`
          },
          {
            heading: 'Transistor Amplifier Test Points (Fig 4-17)',
            content: `In an NPN amplifier:
- **TP1 (Emitter)**: Normal reading ~1.5V to 2.0V DC. If 0V, the transistor is cut off or emitter bypass cap C1 is shorted.
- **TP2 (Base)**: Normal reading ~0.7V higher than TP1 (~2.2V to 2.7V DC). If base voltage equals emitter voltage, the base-emitter junction has failed.
- **TP3 (Collector)**: Normal reading ~half of supply voltage (~6V on a 12V supply). If TP3 reads full +12V, transistor Q1 is not conducting (open emitter resistor R1 or defective Q1). If TP3 reads 0V, collector resistor R4 is open.`
          }
        ],
        quiz: [
          {
            id: 'q-6-1-1',
            prompt: 'In the transistor amplifier of Fig. 4-17 with a +12V power supply, test point TP3 (Collector) reads exactly +12.0 VDC while TP1 (Emitter) reads 0.0 VDC. What is the most probable fault?',
            options: [
              'Transistor Q1 is not conducting current (cut off due to open emitter resistor R1 or blown transistor)',
              'Collector resistor R4 is completely open',
              'Capacitor C1 is shorted to positive supply',
              'The input signal is too high'
            ],
            correctIndex: 0,
            explanation: 'Technical Reference: If TP3 reads full supply (+12V), zero current is flowing through R4 (zero voltage drop across R4). This indicates Q1 is cut off, confirmed by 0V at the emitter.'
          }
        ]
      },
      {
        id: 'les-6-2',
        title: 'Kirchhoff’s Current Law (KCL) in Practice',
        durationMinutes: 18,
        summary: 'Verify the principle of charge conservation: the sum of currents entering any node equals the sum leaving.',
        diagramUrl: '/diagrams/diag_kcl_kvl_breadboard_circuits.svg',
        diagramCaption: 'Figure 6-4 & 6-5 — Verifying Kirchhoff’s Current Law (KCL) on a Resistor Network.',
        sections: [
          {
            heading: 'The Principle of Current Conservation',
            content: `Formulated by Gustav Robert Kirchhoff (1824–1887), Kirchhoff's Current Law (KCL) states:

$$\\sum I_{\\text{entering}} = \\sum I_{\\text{leaving}}$$

Electric charge cannot accumulate at an infinitesimal node. No matter how many circuit branches enter a junction, every milliampere that flows into the branch point MUST flow out through the other branches.`
          },
          {
            heading: 'The Author’s Breadboard Experiment (Fig 6-5)',
            content: `Using a 6.32V battery and five precision resistors:
- Measured entering currents: $I_1 = 10.59\\text{ mA}$ and $I_2 = 2.40\\text{ mA}$.
  $$\\sum I_{\\text{in}} = 10.59 + 2.40 = 12.99\\text{ mA}$$
- Measured leaving currents: $I_3 = 8.35\\text{ mA}$, $I_4 = 2.79\\text{ mA}$, and $I_5 = 1.88\\text{ mA}$.
  $$\\sum I_{\\text{out}} = 8.35 + 2.79 + 1.88 = 13.02\\text{ mA}$$

The tiny 0.03 mA difference (under 0.25% variance) confirms KCL in live hardware.`
          }
        ],
        quiz: [
          {
            id: 'q-6-2-1',
            prompt: 'Three branch wires meet at a single circuit junction. Branch 1 carries 45 mA INTO the junction, and Branch 2 carries 25 mA INTO the junction. What must be the current in Branch 3?',
            options: [
              '70 mA flowing OUT of the junction',
              '20 mA flowing INTO the junction',
              '0 mA',
              '100 mA flowing OUT of the junction'
            ],
            correctIndex: 0,
            explanation: 'By KCL: Total In = Total Out. 45 mA + 25 mA = 70 mA entering; therefore 70 mA must flow out through Branch 3.'
          }
        ]
      },
      {
        id: 'les-6-3',
        title: 'Kirchhoff’s Voltage Law (KVL) in Practice',
        durationMinutes: 18,
        summary: 'Verify the principle of voltage conservation: the algebraic sum of voltages in any closed loop equals zero.',
        diagramUrl: '/diagrams/diag_kcl_kvl_breadboard_circuits.svg',
        diagramCaption: 'Figure 6-6 & 6-8 — Verifying Kirchhoff’s Voltage Law (KVL) on a 4-Resistor Series Circuit.',
        sections: [
          {
            heading: 'The Principle of Voltage Conservation',
            content: `Kirchhoff's Voltage Law (KVL) states that the sum of all electrical potentials (voltages) around any closed loop, taking polarity into account, equals zero:

$$E - (E_1 + E_2 + E_3 + E_4) = 0 \\implies E = E_1 + E_2 + E_3 + E_4$$

The total electromotive force supplied by the battery equals the sum of the individual voltage drops across all series components.`
          },
          {
            heading: 'Experimental Proof on the Breadboard (Fig 6-8)',
            content: `With a 6.30V battery connected across four series resistors:
- $E_1 = 0.82\\text{ V}$ (across 220 Ω)
- $E_2 = 1.22\\text{ V}$ (across 328 Ω)
- $E_3 = 1.75\\text{ V}$ (across 465 Ω)
- $E_4 = 2.52\\text{ V}$ (across 671 Ω)

Sum of drops $= 0.82 + 1.22 + 1.75 + 2.52 = 6.31\\text{ V}$.
The measured battery was $6.30\\text{ V}$, proving KVL within $0.01\\text{ V}$ accuracy.`
          }
        ],
        quiz: [
          {
            id: 'q-6-3-1',
            prompt: 'In a 24V DC series circuit containing three resistors, a voltmeter measures 5V across R1 and 9V across R2. According to KVL, what is the voltage drop across R3?',
            options: [
              '10 Volts',
              '14 Volts',
              '24 Volts',
              '5 Volts'
            ],
            correctIndex: 0,
            explanation: 'By KVL: E_total = E1 + E2 + E3. 24V = 5V + 9V + E3 -> E3 = 24 - 14 = 10V.'
          }
        ]
      },
      {
        id: 'les-6-4',
        title: 'Resistive Voltage Dividers & Load Sag',
        durationMinutes: 20,
        summary: 'Analyze why resistive voltage dividers suffer catastrophic voltage collapse when external loads draw current.',
        diagramUrl: '/diagrams/diag_voltage_divider_vs_diode_reducer.svg',
        diagramCaption: 'Figure 6-13 — Output Voltage Sag as Load Resistance RL Decreases.',
        sections: [
          {
            heading: 'The Unloaded Voltage Divider Formula',
            content: `A voltage divider connects series resistors across a voltage source $E$. The no-load voltage at tap $x$ is:

$$E_x = E \\times \\frac{R_x}{R_{\\text{total}}}$$

This formula is accurate ONLY when no current is drawn from the tap (open circuit).`
          },
          {
            heading: 'The Loading Trap: Why Voltage Sags (Table 6-2)',
            content: `When an external load resistor $R_L$ is connected to the tap, $R_L$ is in **parallel** with the lower divider resistor. This dramatically lowers the effective resistance of the bottom branch.

In the author's experiment (Table 6-2):
- No-load output voltage: **2.04 V**
- With $R_L = 3,250~\\Omega$: Output drops to **1.83 V**
- With $R_L = 672~\\Omega$: Output drops to **1.32 V**
- With $R_L = 220~\\Omega$: Output collapses to **0.76 V**!

A resistive divider cannot provide stable power to variable or heavy industrial loads!`
          }
        ],
        quiz: [
          {
            id: 'q-6-4-1',
            prompt: 'Why is a simple resistive voltage divider unsuitable for powering a DC motor or relay with fluctuating current demands?',
            options: [
              'When the motor draws varying load current, the parallel loading effect causes severe output voltage sag and instability',
              'Resistors cannot pass DC current',
              'The resistors will automatically reverse the motor polarity',
              'A voltage divider only works at 480VAC'
            ],
            correctIndex: 0,
            explanation: 'Technical Reference: Load current shunts the divider resistance, dragging the tap voltage down in proportion to load current.'
          }
        ]
      },
      {
        id: 'les-6-5',
        title: 'Diode-Based Voltage Reducers: Superior Regulation',
        durationMinutes: 18,
        summary: 'Discover how forward-biased silicon diodes deliver rock-solid voltage drops compared to resistive dividers.',
        diagramUrl: '/diagrams/diag_voltage_divider_vs_diode_reducer.svg',
        diagramCaption: 'Figure 6-18 — Rock-Solid Diode Voltage Reducer Characteristic Curve.',
        sections: [
          {
            heading: 'The Constant P-N Junction Drop Principle',
            content: `Unlike a resistor whose voltage drop is directly proportional to current ($V = I \\times R$), a forward-biased silicon diode exhibits a nearly constant voltage drop of ~0.6V to 0.7V once the breakover threshold is passed.

By connecting two silicon rectifier diodes in series with a 6.30V battery:
$$\\text{Total Diode Drop} \\approx 2 \\times 0.7\\text{ V} = 1.4\\text{ V}$$
$$\\text{Output Voltage} \\approx 6.30\\text{ V} - 1.4\\text{ V} = 4.9\\text{ V}$$`
          },
          {
            heading: 'Experimental Contrast: Resistor vs. Diode (Table 6-3)',
            content: `Across the exact same load range ($3,250~\\Omega$ down to $220~\\Omega$):
- The **Resistive Divider** collapsed by **63%** (from 2.04V down to 0.76V).
- The **Diode Reducer** stayed between **5.08V and 4.79V** (less than 6% variance!).

This demonstrates why solid-state semiconductor junctions form the foundation of modern regulated power supplies.`
          }
        ],
        quiz: [
          {
            id: 'q-6-5-1',
            prompt: 'Why does a series pair of forward-biased silicon diodes provide vastly superior voltage reduction under varying loads compared to a resistive divider?',
            options: [
              'Silicon P-N junctions maintain a nearly constant ~0.7V forward breakover drop regardless of significant changes in load current',
              'Diodes have infinite internal resistance',
              'Diodes double the battery capacity',
              'Diodes only conduct during power outages'
            ],
            correctIndex: 0,
            explanation: 'Technical Reference: The logarithmic forward characteristic of a P-N junction keeps the forward voltage drop clamped around 0.7V over wide current swings.'
          }
        ]
      },
      {
        id: 'les-6-6',
        title: 'Mismatched Series Loads & Circuit Failure Modes',
        durationMinutes: 18,
        summary: 'Examine two incandescent lamps in series, calculate VA power, and observe open-circuit vs short-circuit faults.',
        sections: [
          {
            heading: 'Mismatched Series Lamps Experiment (Fig 6-19)',
            content: `When a 6.3V lamp and a 7.5V lamp are connected in series across a battery:
- Because they are in series, the **same current** ($I = 139\\text{ mA}$) flows through both lamps.
- The higher-resistance lamp receives the larger voltage ($E_2 = 3.64\\text{V}$ vs $E_1 = 2.20\\text{V}$).
- Volt-Ampere (VA) Power: $P_1 = 2.20 \\times 0.139 = 0.306\\text{ VA}$; $P_2 = 3.64 \\times 0.139 = 0.506\\text{ VA}$. Total $P = 0.812\\text{ VA}$.`
          },
          {
            heading: 'The Two Classic Series Failure Modes',
            content: `1. **Open-Circuit Fault**: Unscrewing either lamp breaks the single series path. Current drops to 0.00 mA everywhere, and BOTH lamps instantly go dark.
2. **Short-Circuit Fault**: Shorting out lamp N with a jumper drops its voltage to 0V (goes dark). The ENTIRE battery voltage now drops across lamp P alone, causing lamp P to glow with blinding 100% brilliance!

**Takeaway**: In series circuits, a short across one component increases the voltage and thermal stress across every other remaining component in the circuit!`
          }
        ],
        quiz: [
          {
            id: 'q-6-6-1',
            prompt: 'In a series circuit of three identical indicator lamps, what happens to the remaining two lamps if one lamp filament burns open?',
            options: [
              'Both remaining lamps go dark because the series circuit continuity is broken',
              'The remaining two lamps glow twice as bright',
              'The remaining two lamps begin flashing',
              'The power supply fuse blows instantly'
            ],
            correctIndex: 0,
            explanation: 'In a series circuit, there is only one current path. An open circuit at any point prevents current from flowing anywhere in the loop.'
          }
        ]
      },
      {
        id: 'les-6-7',
        title: 'The Compass-Based Galvanometer: Galvanism in Action',
        durationMinutes: 20,
        summary: 'Explore galvanism, electromagnetism, and why compass needle deflection asymptotically approaches 90° max.',
        diagramUrl: '/diagrams/diag_galvanometer_compass_experiment.svg',
        diagramCaption: 'Figure 6-27 & 6-30 — The Compass Galvanometer and Calibration Curve.',
        sections: [
          {
            heading: 'Galvanism: Current Creating Magnetism',
            content: `The production of a magnetic field by electric current is called **galvanism**.
By winding $8\\frac{1}{2}$ turns of enameled magnet wire around a magnetic compass along its North-South axis:
- When zero current flows, the needle points toward geomagnetic North ($0^\\circ$).
- When DC flows through the coil, it generates an orthogonal East-West magnetic field.
- The compass needle aligns with the **vector sum** of Earth's magnetic field and the coil's induced field, deflecting the needle towards the East.`
          },
          {
            heading: 'The 90° Deflection Asymptote (Fig 6-30)',
            content: `Why can the needle never deflect past 90 degrees?
As current increases from 9.3 mA to 173 mA, the needle deflection increases from $7^\\circ$ to $66^\\circ$. Even with infinite current, the coil's field points purely East-West ($90^\\circ$). Because Earth's geomagnetic field always pulls North, the resultant vector can asymptotically approach $90^\\circ$, but can NEVER reach or exceed $90^\\circ$!

By plotting deflection angle vs. current, a technician creates a calibrated analog milliammeter from household items.`
          }
        ],
        quiz: [
          {
            id: 'q-6-7-1',
            prompt: 'In the compass-based galvanometer experiment, why does the needle deflection angle never exceed 90 degrees, regardless of how much current is forced through the coil?',
            options: [
              'The coil\'s induced magnetic field is perpendicular (East-West) to Earth\'s North-South field; the vector sum can only asymptotically approach 90 degrees',
              'The compass needle loses its magnetization at 90 degrees',
              'The glass face prevents physical needle rotation beyond a quarter turn',
              'The copper wire enters superconducting saturation'
            ],
            correctIndex: 0,
            explanation: 'Technical Reference: The deflection is determined by the tangent of the two vector fields: tan(theta) = B_coil / B_earth. Since B_earth is always non-zero and perpendicular, theta can never reach 90 degrees.'
          }
        ]
      }
    ]
  }
];

// ============================================================================
// COMPREHENSIVE 30-QUESTION COURSE FINAL CERTIFICATION EXAM
// ============================================================================
export const FINAL_EXAM_QUESTIONS: CourseQuizQuestion[] = [
  {
    id: 'fe-01',
    prompt: 'When beginning troubleshooting on a completely non-operational industrial system, what is the recommended sequence of technical diagrams to consult?',
    options: [
      'Block diagram first to isolate the bad stage, Schematic diagram second to pinpoint the bad component, Pictorial layout third to locate the physical part',
      'Pictorial diagram first, Block diagram second, Schematic diagram never',
      'Schematic diagram first, Wiring diagram second, Block diagram third',
      'Start by measuring every resistor with an ohmmeter without any diagram'
    ],
    correctIndex: 0,
    explanation: 'Technical Reference: The block diagram isolates the functional section, the schematic pinpoints the component via electrical test points, and the pictorial locates the component on the chassis.'
  },
  {
    id: 'fe-02',
    prompt: 'In modern schematic drafting, two conductor lines cross at right angles with NO dot at the junction. What does this mean?',
    options: [
      'The conductors cross paths in the drawing but do NOT connect electrically in the circuit',
      'The wires are soldered into a permanent electrical node',
      'The wires must be twisted together to prevent electromagnetic interference',
      'The intersection is an earth ground'
    ],
    correctIndex: 0,
    explanation: 'Modern drafting standards (Fig. 3-33): Crossing lines without a dot indicate electrical bypass with no connection.'
  },
  {
    id: 'fe-03',
    prompt: 'What is the primary advantage of using a split-conductor (offset T-junction, Fig. 3-34A) over a simple crossing with a single dot?',
    options: [
      'It prevents misinterpreting a missing dot as an unconnected crossing or an accidental ink speck as a connection',
      'It reduces high-frequency RF skin effect losses',
      'It indicates that one wire carries AC and the other DC',
      'It allows wires to be color-coded in black-and-white prints'
    ],
    correctIndex: 0,
    explanation: 'Technical Reference: Split-conductor junctions eliminate printing and scanning ambiguities where a dot might be degraded or falsely imagined.'
  },
  {
    id: 'fe-04',
    prompt: 'A resistor has color bands: Brown, Black, Red, Gold. What is its nominal resistance and tolerance?',
    options: [
      '1,000 Ω (1 kΩ) ±5%',
      '100 Ω ±10%',
      '10,000 Ω ±5%',
      '120 Ω ±20%'
    ],
    correctIndex: 0,
    explanation: 'Brown = 1, Black = 0, Red = x100. 10 × 100 = 1,000 Ω = 1 kΩ. Gold = ±5% tolerance.'
  },
  {
    id: 'fe-05',
    prompt: 'Under MIL-spec 5-band resistor color codes, what does an Orange 5th band indicate?',
    options: [
      'Maximum drift/failure rate of ±0.01% of rated value after the first 1,000 hours of operation',
      'Rated for 300 Volts dielectric strength',
      'Wirewound high-temperature construction',
      'Negative temperature coefficient'
    ],
    correctIndex: 0,
    explanation: 'Appendix B, Fig. B-2: The 5th band indicates reliability/drift per 1,000 hours: Brown = 1%, Red = 0.1%, Orange = 0.01%, Yellow = 0.001%.'
  },
  {
    id: 'fe-06',
    prompt: 'What catastrophic failure can occur if a polarized electrolytic capacitor is connected with reverse DC polarity in a power supply?',
    options: [
      'The dielectric oxide layer breaks down, causing severe leakage, rapid internal gas generation, and a violent casing explosion',
      'The transformer secondary short-circuits to the primary winding',
      'The capacitor converts to an air-core inductor',
      'The circuit will operate at exactly half voltage'
    ],
    correctIndex: 0,
    explanation: 'Technical Reference: Reverse bias destroys the electrolytic oxide film, causing catastrophic thermal runaway and casing rupture.'
  },
  {
    id: 'fe-07',
    prompt: 'Why is it standard engineering practice to connect the rotor plates of an air variable capacitor directly to circuit ground?',
    options: [
      'To prevent human body capacitance from detuning the circuit when an operator touches the adjustment shaft, and to prevent electric shock',
      'Because rotor plates cannot withstand RF voltages',
      'To double the Q factor of the inductor',
      'To convert the capacitor into a Zener regulator'
    ],
    correctIndex: 0,
    explanation: 'Technical Reference: Grounding the rotor and shaft shields the tuned circuit from hand capacitance.'
  },
  {
    id: 'fe-08',
    prompt: 'How is a powdered-iron core inductor distinguished from a solid iron-core inductor in a schematic?',
    options: [
      'A powdered-iron core is drawn with parallel DASHED (broken) lines above the coil symbol',
      'A powdered-iron core has a solid black bar through the center of the coil',
      'A powdered-iron core uses three concentric circles',
      'A powdered-iron core has an envelope circle surrounding it'
    ],
    correctIndex: 0,
    explanation: 'Technical Reference: Powdered-iron cores use parallel dashed lines; solid/laminated iron cores use parallel solid lines.'
  },
  {
    id: 'fe-09',
    prompt: 'What type of inductor features a rotating bare-wire coil and a rolling wheel contact for smooth, continuous RF antenna tuning?',
    options: [
      'A Roller Inductor',
      'A Laminated Iron Choke',
      'A Bifilar Toroid',
      'A Permeability Tuned Slug'
    ],
    correctIndex: 0,
    explanation: 'Technical Reference: A roller inductor provides smooth, continuous adjustment by rolling a contact wheel along bare coil windings.'
  },
  {
    id: 'fe-10',
    prompt: 'A power transformer has a 120V primary winding and an 18V secondary winding. What is the turns ratio (Np : Ns)?',
    options: [
      '6.67 : 1 (Step-down)',
      '1 : 6.67 (Step-up)',
      '12 : 1',
      '1 : 1'
    ],
    correctIndex: 0,
    explanation: 'Turns ratio = Vp / Vs = 120 / 18 = 6.67 : 1.'
  },
  {
    id: 'fe-11',
    prompt: 'In a relay schematic, what does "normal" mean when designating normally open (NO) and normally closed (NC) contacts?',
    options: [
      'The state of the contacts when NO current flows through the electromagnet coil (unenergized)',
      'The state when the machine operates at rated full load current',
      'The contact condition during emergency stop tripping',
      'The state when ambient temperature is 20 degrees Celsius'
    ],
    correctIndex: 0,
    explanation: 'Technical Reference: In relays, "normal" always means zero current in the control coil.'
  },
  {
    id: 'fe-12',
    prompt: 'What is the typical forward breakover voltage threshold for a general-purpose silicon semiconductor diode?',
    options: [
      'Approximately 0.6 to 0.7 Volts',
      '0.05 Volts',
      '12.0 Volts',
      '120 Volts'
    ],
    correctIndex: 0,
    explanation: 'Technical Reference: Silicon P-N junctions require approximately 0.6V to 0.7V of forward bias before conducting heavily.'
  },
  {
    id: 'fe-13',
    prompt: 'Under what operating condition does a Zener diode function to regulate voltage in a DC power supply?',
    options: [
      'In reverse bias, operating at its designed avalanche breakdown threshold',
      'In forward bias at 0.7 Volts',
      'In high-frequency AC oscillation',
      'As a series fuse'
    ],
    correctIndex: 0,
    explanation: 'Technical Reference: Zener diodes are designed to operate in reverse avalanche breakdown to maintain a stable clamp voltage.'
  },
  {
    id: 'fe-14',
    prompt: 'How do you tell an NPN transistor from a PNP transistor on a schematic diagram?',
    options: [
      'The emitter arrow points outward away from the base on an NPN; the arrow points inward toward the base on a PNP',
      'The collector arrow points inward on an NPN',
      'NPN transistors have two base terminals',
      'PNP transistors are always drawn horizontally'
    ],
    correctIndex: 0,
    explanation: 'Schematic Standards: NPN has emitter arrow pointing out; PNP has emitter arrow pointing in.'
  },
  {
    id: 'fe-15',
    prompt: 'In a Junction Field-Effect Transistor (JFET), how does the Gate electrode modulate current flowing between Source and Drain?',
    options: [
      'Reverse bias on the gate creates a depletion zone that constricts current through the channel, similar to stepping on a garden hose',
      'The gate injects minority charge carriers directly into the collector',
      'The gate heats a filament that emits thermionic electrons',
      'The gate mechanically opens a micro-relay contact'
    ],
    correctIndex: 0,
    explanation: 'FET Operating Principle: The gate applies an electrostatic field that constricts the conductive channel between source and drain.'
  },
  {
    id: 'fe-16',
    prompt: 'What is the fundamental difference between an Enhancement-Mode MOSFET and a Depletion-Mode MOSFET?',
    options: [
      'Enhancement-mode is normally non-conducting (OFF) at zero gate bias; depletion-mode is normally conducting (ON) at zero gate bias',
      'Enhancement-mode only works on AC power supplies',
      'Depletion-mode has three gate electrodes',
      'Enhancement-mode cannot be used in digital circuits'
    ],
    correctIndex: 0,
    explanation: 'Technical Reference: Depletion-mode has a built-in channel at Vgs=0; enhancement-mode requires gate bias to create a conducting channel.'
  },
  {
    id: 'fe-17',
    prompt: 'Why is negative feedback via a resistor connected between output and inverting input universally used in linear op-amp circuits?',
    options: [
      'Without negative feedback (open loop), the immense open gain causes the op-amp to saturate or break into uncontrollable oscillation',
      'Negative feedback steps up the DC supply voltage',
      'Negative feedback prevents input current from flowing to ground',
      'It converts the op-amp into a full-wave rectifier'
    ],
    correctIndex: 0,
    explanation: 'Technical Reference: Negative feedback tames the massive open-loop gain to provide stable, precise closed-loop amplification.'
  },
  {
    id: 'fe-18',
    prompt: 'What formula calculates the cutoff frequency of an op-amp active lowpass or highpass RC filter?',
    options: [
      'fc = 1 / (2πRC)',
      'fc = 2π / (RC)',
      'fc = RC / (2π)',
      'fc = √(2πRC)'
    ],
    correctIndex: 0,
    explanation: 'Technical Reference: Cutoff frequency fc in Hz equals 1 / (2πRC).'
  },
  {
    id: 'fe-19',
    prompt: 'In electron tube symbology, what does a triode tube consist of, and what semiconductor device is it functionally analogous to?',
    options: [
      'Cathode, Control Grid, and Plate; functionally analogous to an N-channel JFET (Source, Gate, Drain)',
      'Emitter, Base, and Collector; analogous to an optoisolator',
      'Two plates and one filament; analogous to a bridge rectifier',
      'Anode and Cathode only; analogous to a Zener diode'
    ],
    correctIndex: 0,
    explanation: 'Vacuum Tube vs Solid State Principle: Triode cathode corresponds to JFET source, control grid to JFET gate, and plate to JFET drain.'
  },
  {
    id: 'fe-20',
    prompt: 'In a digital logic circuit (Fig. 4-24), inverters are placed in series with both inputs X and Y of an AND gate. What compound logic gate does this circuit replicate?',
    options: [
      'A NOR gate (outputs 1 only when BOTH inputs are 0)',
      'An OR gate',
      'A NAND gate',
      'An XOR gate'
    ],
    correctIndex: 0,
    explanation: 'Digital Logic Principle: (NOT X) AND (NOT Y) is identically equivalent to X NOR Y by DeMorgan’s theorem.'
  },
  {
    id: 'fe-21',
    prompt: 'Why must high-voltage DC power supply schematics always include a Bleeder Resistor (R1)?',
    options: [
      'To safely discharge stored high-voltage energy from filter capacitors when power is shut off, preventing fatal electric shocks',
      'To increase ripple frequency to 240 Hz',
      'To limit transformer primary current during lightning surges',
      'To light up a neon indicator lamp'
    ],
    correctIndex: 0,
    explanation: 'Electrical Safety Principle: Capacitors retain dangerous voltage when disconnected unless bled down by a resistor.'
  },
  {
    id: 'fe-22',
    prompt: 'In the complete 3-transistor AM radio receiver (Fig. 5-5), what is the purpose of diode D1?',
    options: [
      'It acts as a detector (demodulator), converting the received RF carrier into pulsating DC containing audio intelligence',
      'It regulates the +12V battery supply voltage',
      'It protects the loudspeaker from reverse polarity',
      'It amplifies audio power by 100 times'
    ],
    correctIndex: 0,
    explanation: 'Technical Reference: Diode D1 demodulates the AM radio wave, separating the audio frequency signal from the RF carrier.'
  },
  {
    id: 'fe-23',
    prompt: 'How does a push-pull power amplifier (Fig. 5-4) achieve high efficiency and sound power?',
    options: [
      'One transistor amplifies the positive half-cycle ("pushes") while the other transistor amplifies the negative half-cycle ("pulls")',
      'Both transistors are wired in parallel to share DC supply current equally',
      'One transistor acts as a power diode and the other as an antenna tuner',
      'It uses high-frequency pulse-width modulation at 100 kHz'
    ],
    correctIndex: 0,
    explanation: 'Technical Reference: In push-pull amplification, each device handles half the AC waveform, recombining in the output transformer.'
  },
  {
    id: 'fe-24',
    prompt: 'When reading a multi-page schematic, you find a wedge arrow labeled "X" pointing off Sheet 1. What does this symbol denote?',
    options: [
      'An off-page connection indicating that the conductor continues directly to the matching wedge "X" on another sheet',
      'An explosive charge or thermal pyrotechnic fuse',
      'A wireless infrared transmitter beam',
      'A test point that must never be connected'
    ],
    correctIndex: 0,
    explanation: 'Technical Reference: Off-page connection wedges labeled X, Y, Z link continuous circuits across page breaks.'
  },
  {
    id: 'fe-25',
    prompt: 'According to Kirchhoff\'s Current Law (KCL), what must be true at any electrical node or branch point in a circuit?',
    options: [
      'The algebraic sum of all currents entering the node must equal the sum of all currents leaving the node',
      'Current entering must always be twice the leaving current',
      'Voltage drops around the node must equal 120 Volts',
      'The current in every branch must be identical'
    ],
    correctIndex: 0,
    explanation: 'KCL (Conservation of Charge): No charge can accumulate at a node; total current in must equal total current out.'
  },
  {
    id: 'fe-26',
    prompt: 'A technician measures four series resistors in a 12V circuit and finds voltage drops of 2V, 3V, and 4V across the first three. By Kirchhoff’s Voltage Law (KVL), what is the voltage across the fourth resistor?',
    options: [
      '3 Volts',
      '12 Volts',
      '9 Volts',
      '1 Volt'
    ],
    correctIndex: 0,
    explanation: 'By KVL: E_total = E1 + E2 + E3 + E4. 12V = 2V + 3V + 4V + E4 -> E4 = 12 - 9 = 3V.'
  },
  {
    id: 'fe-27',
    prompt: 'What happens to the output voltage of a resistive voltage divider when an external load resistor RL is connected across the output tap?',
    options: [
      'The output voltage sags (drops), because the load resistor is in parallel with the lower divider resistor, reducing total branch resistance',
      'The output voltage increases toward the battery voltage',
      'The output voltage remains 100% constant regardless of load resistance',
      'The upper divider resistor acts as a short circuit'
    ],
    correctIndex: 0,
    explanation: 'Technical Reference: Connecting load RL places it in parallel with the tap resistor, causing severe voltage sag under heavy load.'
  },
  {
    id: 'fe-28',
    prompt: 'Why does a diode-based voltage reducer provide much more stable voltage under varying load current than a resistive voltage divider?',
    options: [
      'Forward-biased silicon diodes have a nearly fixed P-N junction breakover voltage (~0.7V) that varies only slightly with current',
      'Diodes store energy in an internal electrostatic field',
      'Diodes operate as active step-up transformers',
      'Diodes have zero internal resistance'
    ],
    correctIndex: 0,
    explanation: 'Technical Reference: The forward breakover voltage of a diode is logarithmic and remains very flat across wide load variations.'
  },
  {
    id: 'fe-29',
    prompt: 'In a series circuit containing two mismatched incandescent lamps (6.3V and 7.5V), what occurs if one lamp is deliberately short-circuited with a jumper wire?',
    options: [
      'The shorted lamp goes dark, and the remaining lamp receives full source voltage, glowing with maximum brilliance',
      'Both lamps go dark immediately',
      'The battery voltage drops to 0.0 Volts permanently',
      'The shorted lamp burns out from excessive voltage'
    ],
    correctIndex: 0,
    explanation: 'Technical Reference: Shorting one component eliminates its voltage drop, applying the full remaining source potential across the second lamp.'
  },
  {
    id: 'fe-30',
    prompt: 'In a compass-based galvanometer, an 8½-turn coil is aligned with Earth’s magnetic North. As DC current through the coil increases to high levels, what happens to the compass needle deflection angle?',
    options: [
      'The needle deflection increases but asymptotically approaches a maximum of 90 degrees (East/West) and can never exceed 90°',
      'The needle spins continuously like a motor',
      'The needle flips upside down at 50 mA',
      'The needle returns to North at 100 mA'
    ],
    correctIndex: 0,
    explanation: 'Technical Reference: The needle points along the resultant vector of Earth\'s field (North) and the coil\'s field (East-West). Since Earth\'s field is always present, the deflection angle can never reach or exceed 90°.'
  }
];
